import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import RestoreOutlinedIcon from "@mui/icons-material/RestoreOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ForwardToInboxOutlinedIcon from "@mui/icons-material/ForwardToInboxOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { toast } from "react-toastify";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { stripSearchQueriesFromUnderwritingMarkdown } from "./utils/underwritingReportSanitizer";
import getTenantIdFromURL from "./utils/getTenantIdFromURL";
import { getCurrentUserId } from "./authUtils";
import { createQuickTask, listQuickTaskAssignees } from "./api/quickTasksApi";
import WebsiteReachabilityBadge from "./components/WebsiteReachabilityBadge";
import {
  getPaymentPlatformEntity,
  getPaymentPlatformEntityFinancials,
  getPaymentPlatformFullUnderwritingJob,
  getPaymentPlatformOfferLift,
  startPaymentPlatformOfferLift,
  createPaymentPlatformOfferLiftInvitation,
  reviewPaymentPlatformEntity,
  setPaymentPlatformEntityArchived,
  startPaymentPlatformFullUnderwriting,
} from "./api/companyApi";

const C = {
  ink: "#0b2a4a",
  blue: "#1e6fe8",
  muted: "#64748b",
  line: "#e3ebf3",
  green: "#12a150",
  amber: "#d97706",
  red: "#dc2626",
  bg: "#f6f9fc",
};
const money = (value) =>
  value == null
    ? "—"
    : `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const valueText = (value) => {
  if (value == null || value === "") return "—";
  if (typeof value === "number")
    return value.toLocaleString(undefined, { maximumFractionDigits: 3 });
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};
const label = (key) =>
  String(key || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
const color = (decision) =>
  decision === "approve"
    ? C.green
    : decision === "review"
      ? C.amber
      : decision === "decline"
        ? C.red
        : C.muted;

const decisionPresentation = {
  approved: { label: "APPROVED", color: C.green, severity: "success" },
  not_approved: { label: "NOT APPROVED", color: C.red, severity: "error" },
  approved_with_conditions: {
    label: "APPROVED WITH CONDITIONS",
    color: C.amber,
    severity: "warning",
  },
};

export const clientSafeAiText = (value) => String(value || "")
  .replace(/\b(?:claude|gemini)-[a-z0-9.-]+\b/gi, "CrediArc AI")
  .replace(/\b(?:Claude|Anthropic|Gemini|ChatGPT|OpenAI)\b/gi, "CrediArc AI")
  .replace(/plus\.dnb\.com/gi, "CrediArc")
  .replace(/Dun\s*&\s*Bradstreet|Dun and Bradstreet|D\s*&\s*B|D-?U-?N-?S|\bDnB\b|\bDNB\b/gi, "CrediArc");

export function extractUnderwritingExplanation(reportMarkdown) {
  const report = String(reportMarkdown || "");
  const decisionSection = report.match(
    /(?:^|\n)#{1,4}\s*(?:1[.)]?\s*)?Decision Summary\s*\n([\s\S]*?)(?=\n#{1,4}\s|$)/i,
  );
  const source = decisionSection?.[1] || "";
  return source
    .replace(/<!--[^]*?-->/g, " ")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[*_`>#|]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 700);
}

export function normalizeUnderwritingMarkdown(reportMarkdown) {
  const rowsExpanded = stripSearchQueriesFromUnderwritingMarkdown(reportMarkdown)
    .replace(/\r\n?/g, "\n")
    .replace(/[ \t]*\|\|[ \t]*/g, "\n");
  const normalized = [];

  rowsExpanded.split("\n").forEach((sourceLine) => {
    const line = sourceLine.trim();
    if (!line) {
      if (normalized.at(-1) !== "") normalized.push("");
      return;
    }
    const numberedHeading = line.match(/^(\d{1,2})([a-z]?)\.\s+([^|]+?)(\s+\|.*)?$/i);
    if (numberedHeading) {
      const [, number, suffix, heading, tableRow] = numberedHeading;
      if (normalized.length && normalized.at(-1) !== "") normalized.push("");
      normalized.push(`${suffix ? "###" : "##"} ${number}${suffix}. ${heading.trim()}`, "");
      if (tableRow) normalized.push(tableRow.trim());
      return;
    }
    normalized.push(line);
  });

  const withTableSpacing = [];
  normalized.forEach((line, index) => {
    const isTableRow = /^\|.*\|?$/.test(line);
    const previousIsTableRow = /^\|.*\|?$/.test(normalized[index - 1] || "");
    if (isTableRow && !previousIsTableRow && withTableSpacing.at(-1) !== "") {
      withTableSpacing.push("");
    }
    if (!isTableRow && previousIsTableRow && line && withTableSpacing.at(-1) !== "") {
      withTableSpacing.push("");
    }
    withTableSpacing.push(line);
  });

  return withTableSpacing.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

const headingText = (children) => React.Children.toArray(children)
  .map((child) => (typeof child === "string" || typeof child === "number" ? String(child) : ""))
  .join(" ")
  .replace(/\s+/g, " ")
  .trim();

const headingId = (value) => `underwriting-${String(value || "section")
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-|-$/g, "")}`;

export function underwritingSectionOutline(reportMarkdown) {
  return normalizeUnderwritingMarkdown(reportMarkdown)
    .split("\n")
    .map((line) => line.match(/^##\s+(.+)$/)?.[1]?.trim())
    .filter((title) => /^SECTION\s+\d+\b/i.test(String(title || "").replace(/[*_`]/g, "").trim()))
    .map((title) => ({ title, id: headingId(title) }));
}

export function underwritingReportMeta(reportMarkdown) {
  const text = markdownCellText(reportMarkdown);
  return {
    mode: text.match(/\bMODE:\s*(.+?)(?=\s+DECISION:|$)/i)?.[1]?.trim() || null,
    decision: text.match(/\bDECISION:\s*(.+?)(?=\s+CONFIDENCE:|$)/i)?.[1]?.trim() || null,
    confidence: text.match(/\bCONFIDENCE:\s*([^\n(]+?)(?=\s*\(|\n|$)/i)?.[1]?.trim() || null,
  };
}

function markdownCellText(value) {
  return String(value || "")
    .replace(/<!--[^]*?-->/g, " ")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[*_`>#]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function tableCells(line) {
  const trimmed = String(line || "").trim().replace(/^\|/, "").replace(/\|$/, "");
  return trimmed.split("|").map(markdownCellText);
}

function numericRecommendationValue(value) {
  const match = String(value || "").match(/-?\d[\d,]*(?:\.\d+)?/);
  if (!match) return null;
  const parsed = Number(match[0].replaceAll(",", ""));
  return Number.isFinite(parsed) ? parsed : null;
}

export function extractNumberedUnderwritingSection(reportMarkdown, sectionNumber) {
  const lines = normalizeUnderwritingMarkdown(reportMarkdown).split("\n");
  const headingPattern = new RegExp(`^#{1,4}\\s*(?:SECTION\\s+)?${sectionNumber}(?:[.)]|\\s|\\s*[—–:-])`, "i");
  const nextHeadingPattern = /^#{1,4}\s*(?:SECTION\s+)?\d{1,2}(?:[.)]|\s|\s*[—–:-])/i;
  const start = lines.findIndex((line) => headingPattern.test(line.trim()));
  if (start < 0) return null;
  const relativeEnd = lines.slice(start + 1).findIndex((line) => nextHeadingPattern.test(line.trim()));
  const sectionLines = lines.slice(start + 1, relativeEnd < 0 ? lines.length : start + 1 + relativeEnd);
  const markdown = sectionLines.join("\n").trim();
  return markdown ? { markdown, plainText: markdownCellText(markdown) } : null;
}

export function extractCompanyResearchProfile(reportMarkdown) {
  return extractNumberedUnderwritingSection(reportMarkdown, 2);
}

export function extractSectionElevenRecommendation(reportMarkdown, preferredTier = null) {
  const lines = normalizeUnderwritingMarkdown(reportMarkdown).split("\n");
  const start = lines.findIndex((line) => /^#{1,4}\s*(?:SECTION\s+)?11(?:[.)]|\s|\s*[—–:-])/i.test(line.trim()));
  if (start < 0) return null;
  const relativeEnd = lines.slice(start + 1).findIndex((line) => /^#{1,4}\s*(?:SECTION\s+)?12(?:[.)]|\s|\s*[—–:-])/i.test(line.trim()));
  const sectionLines = lines.slice(start + 1, relativeEnd < 0 ? lines.length : start + 1 + relativeEnd);
  const plainSection = markdownCellText(sectionLines.join("\n"));

  const headerIndex = sectionLines.findIndex((line) => {
    if (!line.trim().startsWith("|")) return false;
    const headers = tableCells(line).map((cell) => cell.toLowerCase());
    return headers.some((cell) => /^(tier|option|recommendation)$/.test(cell))
      && headers.some((cell) => /(?:advance|funding)?\s*amount/.test(cell));
  });
  if (headerIndex < 0) return null;

  const headers = tableCells(sectionLines[headerIndex]).map((cell) => cell.toLowerCase());
  const tierIndex = headers.findIndex((cell) => /^(tier|option|recommendation)$/.test(cell));
  const amountIndex = headers.findIndex((cell) => /(?:advance|funding)?\s*amount/.test(cell));
  const holdbackIndex = headers.findIndex((cell) => /holdback/.test(cell));
  const rows = [];
  for (const line of sectionLines.slice(headerIndex + 1)) {
    if (!line.trim().startsWith("|")) {
      if (rows.length) break;
      continue;
    }
    const cells = tableCells(line);
    if (cells.every((cell) => /^:?-{3,}:?$/.test(cell))) continue;
    const tierCell = cells[tierIndex] || "";
    const tierMatch = tierCell.match(/\b(conservative|moderate|aggressive)\b/i);
    if (!tierMatch) continue;
    rows.push({
      chosenTier: tierMatch[1][0].toUpperCase() + tierMatch[1].slice(1).toLowerCase(),
      amount: numericRecommendationValue(cells[amountIndex]),
      holdbackPercent: holdbackIndex >= 0 ? numericRecommendationValue(cells[holdbackIndex]) : null,
      markedRecommended: /recommended|selected|chosen/i.test(tierCell),
    });
  }
  if (!rows.length) return null;

  const explicitTier = plainSection.match(/(?:final\s+)?recommendation(?:\s+tier)?\s*:?\s*(?:is\s+)?(conservative|moderate|aggressive)\b/i)?.[1];
  const desiredTier = explicitTier || preferredTier;
  const selected = rows.find((row) => desiredTier && row.chosenTier.toLowerCase() === String(desiredTier).toLowerCase())
    || rows.find((row) => row.markedRecommended);
  if (!selected) return null;

  let decision = "approved_with_conditions";
  if (/\b(not approved|declin(?:e|ed)|no offer|do not fund)\b/i.test(plainSection)) decision = "not_approved";
  else if (/\bapproved\b/i.test(plainSection) && !/\b(conditions?|conditional|indicative|stipulations?)\b/i.test(plainSection)) decision = "approved";
  return { ...selected, decision, source: "section_11" };
}

const underwritingMarkdownComponents = {
  h1: ({ node, ...props }) => (
    <Typography component="h1" {...props} sx={{ color: C.ink, fontSize: { xs: 24, md: 30 }, fontWeight: 950, letterSpacing: "-0.025em", mt: 0, mb: 2 }} />
  ),
  h2: ({ node, children, ...props }) => {
    const title = headingText(children);
    return <Typography id={headingId(title)} component="h2" {...props} sx={{ color: C.ink, fontSize: { xs: 18, md: 20 }, fontWeight: 950, letterSpacing: "-0.015em", mt: 4, mb: 1.25, pb: 1, borderBottom: `1px solid ${C.line}`, scrollMarginTop: 20 }}>{children}</Typography>;
  },
  h3: ({ node, ...props }) => (
    <Typography component="h3" {...props} sx={{ color: "#164e86", fontSize: 16, fontWeight: 900, mt: 2.75, mb: 1 }} />
  ),
  p: ({ node, ...props }) => (
    <Typography component="p" {...props} sx={{ color: "#29445f", fontSize: 14.5, lineHeight: 1.78, my: 1.15 }} />
  ),
  ul: ({ node, ...props }) => <Box component="ul" {...props} sx={{ color: "#29445f", pl: 3, my: 1.25, "& li": { pl: 0.5, mb: 0.7, lineHeight: 1.65 } }} />,
  ol: ({ node, ...props }) => <Box component="ol" {...props} sx={{ color: "#29445f", pl: 3, my: 1.25, "& li": { pl: 0.5, mb: 0.7, lineHeight: 1.65 } }} />,
  blockquote: ({ node, ...props }) => <Box component="blockquote" {...props} sx={{ m: "18px 0", px: 2, py: 1, bgcolor: "#f4f8fd", borderLeft: `4px solid ${C.blue}`, borderRadius: "0 10px 10px 0", "& p": { m: 0 } }} />,
  table: ({ node, ...props }) => (
    <Box sx={{ my: 2, overflowX: "auto", border: `1px solid ${C.line}`, borderRadius: 2.5, boxShadow: "0 8px 24px rgba(17, 50, 83, 0.05)" }}>
      <Box component="table" {...props} sx={{ width: "100%", minWidth: 620, borderCollapse: "separate", borderSpacing: 0 }} />
    </Box>
  ),
  thead: ({ node, ...props }) => <Box component="thead" {...props} sx={{ bgcolor: "#edf4fc" }} />,
  th: ({ node, ...props }) => <Box component="th" {...props} sx={{ color: C.ink, px: 1.5, py: 1.25, textAlign: "left", verticalAlign: "top", fontSize: 11, lineHeight: 1.4, fontWeight: 950, textTransform: "uppercase", letterSpacing: "0.035em", borderBottom: `1px solid ${C.line}`, whiteSpace: "normal" }} />,
  td: ({ node, ...props }) => <Box component="td" {...props} sx={{ color: "#29445f", px: 1.5, py: 1.15, fontSize: 13, lineHeight: 1.5, verticalAlign: "top", borderBottom: `1px solid ${C.line}`, "tr:last-of-type &": { borderBottom: 0 } }} />,
  a: ({ node, ...props }) => <Box component="a" {...props} target="_blank" rel="noopener noreferrer" sx={{ color: C.blue, fontWeight: 700, overflowWrap: "anywhere" }} />,
  code: ({ node, ...props }) => <Box component="code" {...props} sx={{ bgcolor: "#edf2f7", borderRadius: 1, px: 0.6, py: 0.2, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: "0.88em" }} />,
};

const compactUnderwritingMarkdownComponents = {
  ...underwritingMarkdownComponents,
  h2: ({ node, ...props }) => <Typography component="h2" {...props} sx={{ color: C.ink, fontSize: 16, fontWeight: 900, mt: 2, mb: 0.9, textAlign: "left" }} />,
  h3: ({ node, ...props }) => <Typography component="h3" {...props} sx={{ color: C.ink, fontSize: 14, fontWeight: 900, mt: 1.75, mb: 0.75, textAlign: "left" }} />,
  p: ({ node, ...props }) => <Typography component="p" {...props} sx={{ color: "#29445f", fontSize: 12.5, lineHeight: 1.7, my: 0.9, textAlign: "left" }} />,
  ul: ({ node, ...props }) => <Box component="ul" {...props} sx={{ color: "#29445f", pl: 2.75, my: 1, fontSize: 12.5, textAlign: "left", "& li": { pl: 0.35, mb: 0.6, lineHeight: 1.65 } }} />,
  ol: ({ node, ...props }) => <Box component="ol" {...props} sx={{ color: "#29445f", pl: 2.75, my: 1, fontSize: 12.5, textAlign: "left", "& li": { pl: 0.35, mb: 0.6, lineHeight: 1.65 } }} />,
  table: ({ node, ...props }) => (
    <Box sx={{ width: "100%", my: 1.25, overflowX: "auto", border: `1px solid ${C.line}`, borderRadius: 2, bgcolor: "#fff" }}>
      <Box component="table" {...props} sx={{ width: "100%", minWidth: 760, borderCollapse: "collapse", tableLayout: "auto" }} />
    </Box>
  ),
  th: ({ node, ...props }) => <Box component="th" {...props} sx={{ color: C.ink, bgcolor: "#edf4fc", px: 1.25, py: 1, textAlign: "left", verticalAlign: "top", fontSize: 10.5, fontWeight: 900, lineHeight: 1.4, borderBottom: `1px solid ${C.line}`, whiteSpace: "normal" }} />,
  td: ({ node, ...props }) => <Box component="td" {...props} sx={{ color: "#29445f", px: 1.25, py: 1, textAlign: "left", verticalAlign: "top", fontSize: 11.5, lineHeight: 1.5, borderBottom: `1px solid ${C.line}`, "tr:last-of-type &": { borderBottom: 0 } }} />,
};

function UnderwritingMarkdownBlock({ markdown, fallback, testId }) {
  return (
    <Box
      data-testid={testId}
      sx={{
        minWidth: 0,
        mt: 0.65,
        textAlign: "left",
        overflowWrap: "anywhere",
        "& > :first-of-type": { mt: 0 },
        "& > :last-child": { mb: 0 },
      }}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={compactUnderwritingMarkdownComponents}>
        {markdown || fallback}
      </ReactMarkdown>
    </Box>
  );
}

export function buildRecommendationPresentation(entity = {}) {
  entity = entity || {};
  const assessment = entity.assessment || {};
  const recommendation = assessment.recommendation || {};
  const llm = entity.fullUnderwriting?.recommendationSummary || {};
  const lockedTerms = entity.commercialTerms?.locked ? entity.commercialTerms : {};
  const sectionEleven = extractSectionElevenRecommendation(
    entity.fullUnderwriting?.reportMarkdown,
    llm.chosenTier,
  ) || {};
  let decision = sectionEleven.decision || llm.decision;
  if (!decisionPresentation[decision]) {
    if (assessment.decision === "approve") {
      decision = recommendation.requiresManualReview
        ? "approved_with_conditions"
        : "approved";
    } else if (
      assessment.decision === "review" &&
      recommendation.amount != null
    ) {
      decision = "approved_with_conditions";
    } else {
      decision = "not_approved";
    }
  }
  const fallbackConditions = [
    ...(assessment.guardrailHits || []),
    ...(assessment.missingFeatures || []).map(
      (feature) => `Verify ${label(feature).toLowerCase()}`,
    ),
  ];
  const conditions = (llm.conditions?.length ? llm.conditions : fallbackConditions)
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .slice(0, 12);
  const maxPaymentShare = Number(recommendation.maxPaymentShare);
  const fallbackHoldback = Number.isFinite(maxPaymentShare)
    ? maxPaymentShare <= 1
      ? maxPaymentShare * 100
      : maxPaymentShare
    : null;
  const explanation =
    llm.shortExplanation ||
    extractUnderwritingExplanation(entity.fullUnderwriting?.reportMarkdown) ||
    `The current assessment produced a ${label(assessment.decision || "unscored").toLowerCase()} result based on ${assessment.coverage || 0}% evidence coverage. Review the detailed report and source evidence before taking a funding action.`;
  return {
    decision,
    ...decisionPresentation[decision],
    amount:
      sectionEleven.amount ??
      llm.recommendedAmount ??
      (["approve", "review"].includes(assessment.decision)
        ? recommendation.amount
        : null),
    factorRate: lockedTerms.factorRate ?? llm.recommendedFactorRate ?? recommendation.factorRate ?? null,
    holdbackPercent: lockedTerms.remittanceRate ?? sectionEleven.holdbackPercent ?? llm.recommendedHoldbackPercent ?? fallbackHoldback,
    creditScore: llm.recommendedCreditScore ?? assessment.score ?? null,
    conditions,
    explanation,
    chosenTier: sectionEleven.chosenTier || llm.chosenTier || null,
  };
}

export function buildScoreExplainability(entity = {}) {
  const assessment = entity?.assessment || {};
  const evidence = (assessment.evidence || []).map((item) => {
    const normalizedValue = Number(item.value);
    const weight = Number(item.weight);
    const contribution = normalizedValue * weight;
    return {
      feature: item.feature,
      normalizedValue,
      weight,
      contribution: Number.isFinite(contribution) ? contribution : 0,
    };
  }).filter((item) => Number.isFinite(item.normalizedValue) && Number.isFinite(item.weight) && item.weight > 0);
  const observedWeight = evidence.reduce((sum, item) => sum + item.weight, 0);
  const weightedPoints = evidence.reduce((sum, item) => sum + item.contribution, 0);
  const evidenceScore = observedWeight > 0 ? (weightedPoints / observedWeight) * 100 : null;
  const coverage = Number(assessment.coverage || 0);
  const configuredWeight = observedWeight > 0 && coverage > 0 ? observedWeight / (coverage / 100) : null;
  const modelScore = assessment.score == null ? null : Number(assessment.score);
  const displayedScore = buildRecommendationPresentation(entity).creditScore;
  const fullUnderwritingSection = extractNumberedUnderwritingSection(
    entity?.fullUnderwriting?.reportMarkdown,
    10,
  );
  const hasFullUnderwriting = entity?.fullUnderwriting?.status === "done"
    && Boolean(entity?.fullUnderwriting?.recommendationSummary || entity?.fullUnderwriting?.reportMarkdown);
  const fullUnderwritingOverride = displayedScore != null && modelScore != null
    && Math.abs(Number(displayedScore) - modelScore) >= 0.05;
  return {
    evidence,
    observedWeight,
    weightedPoints,
    evidenceScore,
    configuredWeight,
    coverage,
    modelScore,
    displayedScore,
    hasFullUnderwriting,
    fullUnderwritingSection,
    fullUnderwritingExplanation: entity?.fullUnderwriting?.recommendationSummary?.shortExplanation
      || extractUnderwritingExplanation(entity?.fullUnderwriting?.reportMarkdown)
      || null,
    ml: assessment.ml?.applied ? assessment.ml : null,
    fullUnderwritingOverride,
    missingFeatures: assessment.missingFeatures || [],
  };
}

export function buildLimitExplainability(entity = {}) {
  const summary = buildRecommendationPresentation(entity);
  const signals = entity?.signals || {};
  const monthlyVolume = signals.averageMonthlyVolume
    ?? signals.avgMonthlyVolume
    ?? signals.monthlyVolume
    ?? signals.averageMonthlyRevenue
    ?? null;
  const requestedAmount = signals.requestedAmount ?? signals.advanceAmount ?? null;
  const sectionEleven = extractSectionElevenRecommendation(
    entity?.fullUnderwriting?.reportMarkdown,
    entity?.fullUnderwriting?.recommendationSummary?.chosenTier,
  );
  const sectionElevenDetail = extractNumberedUnderwritingSection(
    entity?.fullUnderwriting?.reportMarkdown,
    11,
  );
  const llmAmount = entity?.fullUnderwriting?.recommendationSummary?.recommendedAmount;
  const amountSource = sectionEleven?.amount != null
    ? "Section 11 selected recommendation"
    : llmAmount != null
      ? "Full underwriting recommendation"
      : summary.amount != null
        ? "Configured policy sizing"
        : "Decision gate";
  return {
    amount: summary.amount,
    decision: summary.decision,
    decisionLabel: summary.label,
    decisionColor: summary.color,
    monthlyVolume,
    requestedAmount,
    riskBand: entity?.assessment?.recommendation?.riskBand || null,
    chosenTier: summary.chosenTier,
    holdbackPercent: summary.holdbackPercent,
    amountSource,
    hasFullUnderwriting: entity?.fullUnderwriting?.status === "done"
      && Boolean(entity?.fullUnderwriting?.recommendationSummary || entity?.fullUnderwriting?.reportMarkdown),
    sectionElevenDetail,
    explanation: summary.explanation,
    conditions: summary.conditions,
  };
}

export function buildRecommendationExplainability(entity = {}) {
  const summary = entity?.fullUnderwriting?.recommendationSummary || {};
  const presentation = buildRecommendationPresentation(entity);
  const sectionTen = extractNumberedUnderwritingSection(entity?.fullUnderwriting?.reportMarkdown, 10);
  const sectionEleven = extractNumberedUnderwritingSection(entity?.fullUnderwriting?.reportMarkdown, 11);
  return {
    hasFullUnderwriting: entity?.fullUnderwriting?.status === "done"
      && Boolean(entity?.fullUnderwriting?.recommendationSummary || entity?.fullUnderwriting?.reportMarkdown),
    decisionRationale: summary.decisionRationale || presentation.explanation || null,
    scoreRationale: summary.scoreRationale || sectionTen?.plainText || null,
    limitRationale: summary.limitRationale || sectionEleven?.plainText || null,
    keyDrivers: (summary.keyDrivers || []).map((item) => String(item || "").trim()).filter(Boolean),
    whatWouldChangeRecommendation: (summary.whatWouldChangeRecommendation || []).map((item) => String(item || "").trim()).filter(Boolean),
  };
}

function Metric({ title, value, detail }) {
  return (
    <Card
      variant="outlined"
      sx={{ p: 2, borderRadius: 3, borderColor: C.line }}
    >
      <Typography
        sx={{
          color: C.muted,
          fontSize: 11,
          fontWeight: 900,
          textTransform: "uppercase",
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{ color: C.ink, fontSize: 24, lineHeight: 1.25, fontWeight: 950 }}
      >
        {value}
      </Typography>
      {detail && (
        <Typography sx={{ color: C.muted, fontSize: 12 }}>{detail}</Typography>
      )}
    </Card>
  );
}

const arcScorePresentation = (band) => ({
  healthy: { label: "Healthy", color: "#16864b", bg: "#eaf8f0" },
  watch: { label: "Watch", color: "#8a6a0a", bg: "#fff8df" },
  elevated: { label: "Elevated health risk", color: "#b75a21", bg: "#fff1e8" },
  high: { label: "High health risk", color: "#c24131", bg: "#fff0ed" },
  critical: { label: "Critical health risk", color: "#a91f2a", bg: "#ffecef" },
}[band] || { label: "Health score available", color: C.blue, bg: "#edf5ff" });

export function ArcScoreCard({ arcScore, underwritingStatus }) {
  if (!arcScore) {
    const processing = ["queued", "running"].includes(underwritingStatus);
    return (
      <Card data-testid="arc-score-card" variant="outlined" sx={{ p: 2.25, mb: 1.75, borderRadius: 2.5, borderColor: C.line }}>
        <Typography sx={{ color: C.ink, fontWeight: 900 }}>ArcScore</Typography>
        <Typography sx={{ color: C.muted, mt: 0.5, fontSize: 12.5, lineHeight: 1.55 }}>
          {processing
            ? "ArcScore will appear when the full MCA report and external-evidence analysis finish."
            : "Run full underwriting to calculate business health from MCA performance, CrediArc financial intelligence, news, web, social, UCC, registration, and public-record evidence."}
        </Typography>
      </Card>
    );
  }
  const score = Math.max(1, Math.min(10, Number(arcScore.value) || 1));
  const position = ((score - 1) / 9) * 100;
  const presentation = arcScorePresentation(arcScore.healthBand);
  const sources = arcScore.evidenceCoverage?.availableSources || [];
  const missingSources = arcScore.evidenceCoverage?.missingSources || [];
  return (
    <Card data-testid="arc-score-card" variant="outlined" sx={{ mb: 1.75, borderRadius: 2.5, borderColor: C.line, overflow: "hidden" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(245px,.8fr) minmax(0,1.6fr)" } }}>
        <Box sx={{ p: 2.5, borderRight: { md: `1px solid ${C.line}` }, borderBottom: { xs: `1px solid ${C.line}`, md: 0 } }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "center" }}>
            <Typography sx={{ color: C.ink, fontWeight: 950, fontSize: 18 }}>ArcScore</Typography>
            <Chip size="small" label="AI health score" variant="outlined" sx={{ color: C.blue, borderColor: "#bcd2f2" }} />
          </Box>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, mt: 1.25 }}>
            <Typography sx={{ color: presentation.color, fontSize: 52, lineHeight: 1, fontWeight: 950, letterSpacing: "-.05em" }}>{score.toFixed(1)}</Typography>
            <Typography sx={{ color: C.muted, fontWeight: 800 }}>/ 10</Typography>
          </Box>
          <Chip size="small" label={presentation.label} sx={{ mt: 1, color: presentation.color, bgcolor: presentation.bg, fontWeight: 900 }} />
          <Box sx={{ position: "relative", height: 9, borderRadius: 99, mt: 2.25, background: "linear-gradient(90deg,#43a866 0%,#a8bd54 35%,#e1ac40 62%,#d96843 82%,#bd3540 100%)" }}>
            <Box sx={{ position: "absolute", left: `${position}%`, top: "50%", width: 17, height: 17, borderRadius: "50%", bgcolor: presentation.color, border: "3px solid #fff", boxShadow: `0 0 0 1px ${presentation.color}`, transform: "translate(-50%,-50%)" }} />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.75 }}>
            <Typography sx={{ color: C.muted, fontSize: 10 }}>1 · Healthy</Typography>
            <Typography sx={{ color: C.muted, fontSize: 10 }}>10 · At risk</Typography>
          </Box>
          <Typography sx={{ color: C.muted, fontSize: 10.5, mt: 1.5 }}>Business health only · MCA eligibility cutoffs excluded</Typography>
        </Box>
        <Box sx={{ p: 2.5, minWidth: 0 }}>
          <Typography sx={{ color: C.ink, fontSize: 13.5, lineHeight: 1.55 }}>{arcScore.summary || "CrediArc AI completed the business-health assessment."}</Typography>
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 1.5 }}>
            {sources.map((source) => <Chip key={source} size="small" variant="outlined" label={clientSafeAiText(source)} />)}
            {missingSources.map((source) => <Chip key={`missing-${source}`} size="small" variant="outlined" label={`${clientSafeAiText(source)} unavailable`} sx={{ color: C.muted, borderStyle: "dashed" }} />)}
            <Chip size="small" variant="outlined" label={`${arcScore.confidence || 0}% confidence`} />
            <Chip size="small" variant="outlined" label={`${arcScore.evidenceCoverage?.percent || 0}% source coverage`} />
          </Box>
          {!!arcScore.drivers?.length && <Box sx={{ mt: 1.5 }}>
            {arcScore.drivers.slice(0, 6).map((driver, index) => (
              <Box key={`${driver.source}-${index}`} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "105px 1fr auto" }, gap: 1, py: 0.9, borderTop: `1px solid ${C.line}`, alignItems: "center" }}>
                <Typography sx={{ color: C.muted, fontSize: 11.5, fontWeight: 850 }}>{clientSafeAiText(driver.source)}</Typography>
                <Typography sx={{ color: C.ink, fontSize: 12 }}>{clientSafeAiText(driver.finding)}</Typography>
                <Typography sx={{ color: driver.impact === "positive" ? C.green : driver.impact === "negative" ? C.red : C.muted, fontSize: 10.5, fontWeight: 850, textTransform: "capitalize", whiteSpace: "nowrap" }}>{driver.severity} {driver.impact}</Typography>
              </Box>
            ))}
          </Box>}
          <Typography sx={{ color: C.muted, fontSize: 10.5, mt: 1.25 }}>
            {arcScore.generatedAt ? `Generated ${new Date(arcScore.generatedAt).toLocaleString()}` : "Generated after full underwriting"}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
}

function SummaryCell({ title, value, detail, accent }) {
  return (
    <Box sx={{ px: { xs: 1.4, md: 1.75 }, py: 1.3, minWidth: 0, borderRight: { xs: 0, sm: `1px solid ${C.line}` }, "&:last-of-type": { borderRight: 0 } }}>
      <Typography sx={{ color: C.muted, fontSize: 10.5, fontWeight: 800 }}>{title}</Typography>
      <Typography sx={{ color: accent || C.ink, fontSize: 17, lineHeight: 1.35, fontWeight: 850, overflowWrap: "anywhere" }}>{value}</Typography>
      {detail && <Typography sx={{ color: C.muted, fontSize: 10.5 }}>{detail}</Typography>}
    </Box>
  );
}

function HeaderMetric({ title, value, progress }) {
  const normalizedProgress = Number.isFinite(Number(progress))
    ? Math.max(0, Math.min(100, Number(progress)))
    : 0;
  return (
    <Box sx={{ minWidth: 0, flex: "1 1 150px", px: { xs: 0, md: 2.25 }, py: { xs: 1, md: 0.25 }, borderRight: { xs: 0, md: `1px solid ${C.line}` }, "&:first-of-type": { pl: { md: 0 } } }}>
      <Typography sx={{ color: C.muted, fontSize: 10.5, fontWeight: 800 }}>{title}</Typography>
      <Typography sx={{ color: C.ink, fontSize: 18, lineHeight: 1.35, fontWeight: 900 }}>{value}</Typography>
      <Box sx={{ mt: 0.65, width: "100%", maxWidth: 180, height: 4, borderRadius: 99, bgcolor: "#e6ebf2", overflow: "hidden" }}>
        <Box sx={{ width: `${normalizedProgress}%`, height: "100%", borderRadius: "inherit", bgcolor: C.ink }} />
      </Box>
    </Box>
  );
}

function TabIntro({ title, subtitle, action }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 2 }}>
      <Box>
        <Typography component="h2" sx={{ color: C.ink, fontSize: 19, fontWeight: 850, letterSpacing: "-0.015em" }}>{title}</Typography>
        <Typography sx={{ color: C.muted, fontSize: 12.5 }}>{subtitle}</Typography>
      </Box>
      {action}
    </Box>
  );
}
function FieldGrid({ data, empty = "No data was supplied." }) {
  const entries = Object.entries(data || {}).filter(
    ([, value]) => value !== null && value !== undefined && value !== "",
  );
  if (!entries.length)
    return <Typography sx={{ color: C.muted }}>{empty}</Typography>;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)" },
        gap: 1,
      }}
    >
      {entries.map(([key, value]) => (
        <Box
          key={key}
          sx={{
            p: 1.35,
            border: `1px solid ${C.line}`,
            borderRadius: 2,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              color: C.muted,
              fontSize: 10.5,
              fontWeight: 850,
              textTransform: "uppercase",
            }}
          >
            {label(key)}
          </Typography>
          <Typography
            title={valueText(value)}
            sx={{
              color: C.ink,
              fontSize: 13.5,
              fontWeight: 650,
              overflowWrap: "anywhere",
            }}
          >
            {valueText(value)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function DataRows({ data, empty = "No data was supplied." }) {
  const entries = Object.entries(data || {}).filter(([, value]) => value !== null && value !== undefined && value !== "");
  if (!entries.length) return <Typography sx={{ color: C.muted, fontSize: 13 }}>{empty}</Typography>;
  return (
    <Box>
      {entries.map(([key, value]) => (
        <Box key={key} sx={{ display: "grid", gridTemplateColumns: "minmax(120px, 0.8fr) minmax(0, 1.2fr)", gap: 2, alignItems: "center", py: 1.15, borderBottom: `1px solid ${C.line}`, "&:last-child": { borderBottom: 0 } }}>
          <Typography sx={{ color: C.muted, fontSize: 12 }}>{label(key)}</Typography>
          <Typography sx={{ color: C.ink, fontSize: 12.5, fontWeight: 750, textAlign: "right", overflowWrap: "anywhere" }}>{valueText(value)}</Typography>
        </Box>
      ))}
    </Box>
  );
}

// `action` renders a node in the trailing column instead of the plain status text —
// used for the website reachability pill, which needs its own controls.
function ChecklistRow({ title, detail, status, ready = false, action = null }) {
  const tone = ready ? C.green : C.amber;
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "26px minmax(0, 1fr) auto", gap: 1.15, alignItems: "center", py: 1.15, borderBottom: `1px solid ${C.line}`, "&:last-child": { borderBottom: 0 } }}>
      <Box sx={{ width: 24, height: 24, display: "grid", placeItems: "center", borderRadius: "50%", color: tone, bgcolor: `${tone}14`, fontSize: 13, fontWeight: 900 }}>{ready ? "✓" : "!"}</Box>
      <Box><Typography sx={{ color: C.ink, fontSize: 12.5, fontWeight: 750 }}>{title}</Typography>{detail ? <Typography sx={{ color: C.muted, fontSize: 10.5 }}>{detail}</Typography> : null}</Box>
      {action || <Typography sx={{ color: C.muted, fontSize: 10.5 }}>{status}</Typography>}
    </Box>
  );
}

function flattenEvidence(value, prefix = "", rows = [], depth = 0) {
  if (rows.length >= 180 || depth > 8 || value === null || value === undefined || value === "") return rows;
  if (Array.isArray(value)) {
    if (!value.length && prefix) rows.push([prefix, "None found"]);
    value.forEach((item, index) => flattenEvidence(item, `${prefix} ${index + 1}`.trim(), rows, depth + 1));
    return rows;
  }
  if (typeof value === "object") {
    Object.entries(value).forEach(([key, item]) => {
      if (["retrievedAt", "command", "log"].includes(key)) return;
      flattenEvidence(item, [prefix, label(key)].filter(Boolean).join(" · "), rows, depth + 1);
    });
    return rows;
  }
  rows.push([prefix || "Evidence", value]);
  return rows;
}

function EvidenceDetails({ title, subtitle, data }) {
  const rows = flattenEvidence(data);
  if (!rows.length) return null;
  return (
    <Section title={title} subtitle={subtitle}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 1 }}>
        {rows.map(([name, value], index) => (
          <Box key={`${name}-${index}`} sx={{ p: 1.35, border: `1px solid ${C.line}`, borderRadius: 2, minWidth: 0 }}>
            <Typography sx={{ color: C.muted, fontSize: 10.5, fontWeight: 850, textTransform: "uppercase" }}>{name}</Typography>
            {typeof value === "string" && /^https?:\/\//i.test(value) ? (
              <Typography component="a" href={value} target="_blank" rel="noreferrer" sx={{ color: C.blue, fontSize: 13.5, fontWeight: 700, overflowWrap: "anywhere" }}>{value}</Typography>
            ) : (
              <Typography sx={{ color: C.ink, fontSize: 13.5, fontWeight: 650, overflowWrap: "anywhere" }}>{valueText(value)}</Typography>
            )}
          </Box>
        ))}
      </Box>
    </Section>
  );
}

function findUccEnrichment(fullUnderwriting = {}) {
  if (fullUnderwriting.uccAudit) return fullUnderwriting.uccAudit;
  const registration = fullUnderwriting.evidenceDetails?.registrationAndUcc || {};
  return registration.officialStateScraper?.data
    || registration.stored?.data?.uccLiensEnrichment
    || registration.registryService?.data
    || null;
}

export function buildUccFreshnessPresentation(fullUnderwriting = {}) {
  const audit = findUccEnrichment(fullUnderwriting);
  if (!audit) return null;
  const records = Array.isArray(audit.records) ? audit.records : [];
  const filingCount = Number.isFinite(Number(audit.filingCount))
    ? Number(audit.filingCount)
    : records.filter((record) => record?.filing_number || record?.filingNumber || record?.file_number || record?.fileNumber).length;
  const status = String(audit.status || "").toLowerCase();
  const outcome = String(audit.outcome || "").toLowerCase();
  const blocked = status === "error" || outcome === "blocked";
  const noLiens = outcome === "verified_no_liens"
    || status === "no_match"
    || records.some((record) => ["no_liens_found", "no_lien_found", "zero_filings"].includes(String(record?.ucc_status || record?.lien_status || "").toLowerCase()));
  const cacheHit = audit.cacheHit === true;
  const live = audit.forceFresh === true && !cacheHit && !blocked;
  let severity = "info";
  let title = "Stored UCC result";
  if (blocked) {
    severity = "error";
    title = "Fresh UCC search blocked — status unknown";
  } else if (cacheHit) {
    severity = "warning";
    title = "Cached UCC result returned";
  } else if (live && noLiens) {
    severity = "success";
    title = "Live UCC search completed — no liens found";
  } else if (live && filingCount > 0) {
    severity = "warning";
    title = `Live UCC search completed — ${filingCount} filing${filingCount === 1 ? "" : "s"} found`;
  } else if (live) {
    severity = "success";
    title = "Live UCC search completed";
  } else if (noLiens) {
    title = "Stored UCC result — no liens found";
  }
  return {
    severity,
    title,
    searchedAt: audit.searchedAt || audit.completedAt || audit.retrievedAt || null,
    queryName: audit.queryName || audit.request?.business_legal_name || null,
    jurisdiction: audit.jurisdiction || audit.request?.personal_state || null,
    runId: audit.runId || null,
    portal: audit.portal || null,
    transportStatus: audit.transportStatus || null,
    filingCount,
    cacheHit,
    live,
    blocked,
    error: audit.error || null,
  };
}

function UccFreshnessSummary({ fullUnderwriting }) {
  const freshness = buildUccFreshnessPresentation(fullUnderwriting);
  if (!freshness) return null;
  const details = [
    freshness.searchedAt ? `Searched ${new Date(freshness.searchedAt).toLocaleString()}` : null,
    freshness.queryName ? `Query: ${freshness.queryName}` : null,
    freshness.jurisdiction ? `Jurisdiction: ${freshness.jurisdiction}` : null,
    freshness.runId ? `Run: ${freshness.runId}` : null,
  ].filter(Boolean);
  return (
    <Section title="UCC search freshness" subtitle="Proof of when and how the lien search used by this report was performed.">
      <Alert severity={freshness.severity} sx={{ borderRadius: 2.5 }}>
        <Typography sx={{ fontWeight: 900 }}>{freshness.title}</Typography>
        {!!details.length && <Typography sx={{ mt: 0.4, fontSize: 12.5 }}>{details.join(" · ")}</Typography>}
        {freshness.error && <Typography sx={{ mt: 0.4, fontSize: 12.5 }}>{freshness.error}</Typography>}
      </Alert>
    </Section>
  );
}

function financialReportingEvidence(value) {
  if (Array.isArray(value)) return value.map(financialReportingEvidence);
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value)
      .filter(([key]) => !/^(?:matchedDuns|duns|dunsNumber|provider|blockIDs?)$/i.test(key))
      .map(([key, item]) => [key, financialReportingEvidence(item)]));
  }
  if (typeof value === "string") return value
    .replace(/plus\.dnb\.com/gi, "CrediArc")
    .replace(/Dun\s*&\s*Bradstreet|Dun and Bradstreet|D\s*&\s*B|D-?U-?N-?S|\bDnB\b|\bDNB\b/gi, "CrediArc");
  return value;
}

function evidenceCoverageLabels(value = {}) {
  const { dnb, ...coverage } = value;
  return dnb === undefined ? coverage : { ...coverage, financialReporting: dnb };
}

function validHttpUrl(value) {
  if (typeof value !== "string" || !/^https?:\/\//i.test(value)) return null;
  try {
    return new URL(value).toString();
  } catch (error) {
    return null;
  }
}

function collectEvidenceUrls(value, rows = [], depth = 0) {
  if (depth > 8 || rows.length >= 80 || value == null) return rows;
  if (typeof value === "string") {
    const url = validHttpUrl(value);
    if (url) rows.push(url);
    return rows;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectEvidenceUrls(item, rows, depth + 1));
    return rows;
  }
  if (typeof value === "object") {
    Object.values(value).forEach((item) => collectEvidenceUrls(item, rows, depth + 1));
  }
  return rows;
}

function collectEvidenceFindings(value, rows = [], depth = 0) {
  if (depth > 8 || rows.length >= 8 || value == null) return rows;
  if (Array.isArray(value)) {
    value.forEach((item) => collectEvidenceFindings(item, rows, depth + 1));
    return rows;
  }
  if (typeof value !== "object") return rows;
  if (typeof value.finding === "string" && value.finding.trim()) {
    rows.push({
      finding: value.finding.trim(),
      sourceName: value.sourceName || value.source || null,
      sourceUrl: validHttpUrl(value.sourceUrl),
      status: value.status || null,
    });
  }
  Object.values(value).forEach((item) => collectEvidenceFindings(item, rows, depth + 1));
  return rows;
}

const SOCIAL_PLATFORMS = ["Instagram", "Facebook", "YouTube", "TikTok", "LinkedIn"];
const SOCIAL_METRIC_KEYS = {
  follower: "Followers", followers: "Followers", followercount: "Followers", followerscount: "Followers",
  following: "Following", followings: "Following", followingcount: "Following", followscount: "Following",
  post: "Posts", posts: "Posts", postcount: "Posts", postscount: "Posts", mediacount: "Posts",
  like: "Likes", likes: "Likes", likecount: "Likes", likescount: "Likes", totallikes: "Likes", heartcount: "Likes",
  view: "Views", views: "Views", viewcount: "Views", viewscount: "Views", totalviews: "Views",
  subscriber: "Subscribers", subscribers: "Subscribers", subscribercount: "Subscribers", subscriberscount: "Subscribers",
  // YouTube names its channel totals differently from every other platform, so they
  // were collected but never matched a metric — the panel showed a channel with
  // millions of subscribers as having no audience data at all.
  numberofsubscribers: "Subscribers", channeltotalviews: "Views", channeltotalvideos: "Videos",
  // LinkedIn's only quantitative field from the current scrape. A bucket string
  // ("10,001+ employees") rather than a count, so it renders as text — without it the
  // LinkedIn card shows nothing at all.
  companysize: "Company size",
  video: "Videos", videos: "Videos", videocount: "Videos", videoscount: "Videos",
  review: "Reviews", reviews: "Reviews", reviewcount: "Reviews", reviewscount: "Reviews",
  rating: "Rating", starrating: "Rating", averagerating: "Rating",
  comment: "Comments", comments: "Comments", commentcount: "Comments", commentscount: "Comments",
  share: "Shares", shares: "Shares", sharecount: "Shares", sharescount: "Shares",
  reaction: "Reactions", reactions: "Reactions", reactioncount: "Reactions", reactionscount: "Reactions",
  impression: "Impressions", impressions: "Impressions", impressioncount: "Impressions", impressionscount: "Impressions",
  connection: "Connections", connections: "Connections", connectioncount: "Connections", connectionscount: "Connections",
  engagementrate: "Engagement rate",
};

// Keys whose value is the timestamp of the account's most recent activity. Follower
// counts say how big an audience is; these say whether the business is still using it,
// which is the sharper signal — a large dormant account and a large active one read
// identically without this.
const LAST_ACTIVITY_KEYS = new Set([
  "latestposttimestamp",
  "latestvideotimestamp",
  "latestigtvvideostimestamp",
  "lastpostdate",
  "lastposttimestamp",
  "createtimeiso",
]);
// Guard rails for a parsed timestamp: social platforms predate none of this, and a
// future date means we misread some other field as a date.
const LAST_ACTIVITY_MIN = Date.UTC(2004, 0, 1);

/**
 * Render recency as elapsed time rather than a date. "8 months ago" is the fact an
 * underwriter is after; a raw date makes them do the subtraction. Kept under the
 * 32-character cap addSocialMetric enforces.
 */
function formatLastActivity(time) {
  const days = Math.floor((Date.now() - time) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  const years = Math.floor(days / 365);
  return years === 1 ? "1yr ago" : `${years}yrs ago`;
}

function parseActivityDate(value) {
  if (value == null || typeof value === "boolean") return null;
  const time = new Date(value).getTime();
  if (!Number.isFinite(time)) return null;
  if (time < LAST_ACTIVITY_MIN || time > Date.now() + 86400000) return null;
  return time;
}

/**
 * Walk the evidence for per-platform "most recent activity" timestamps.
 *
 * Mirrors collectSocialMetrics' traversal so platform attribution matches: the key
 * itself (`latestPostTimestamp`) never names its platform, so it is inherited from the
 * enclosing object's `source`/`url`/`platform` context.
 */
function collectLastActivity(value, target = {}, inheritedPlatform = null, depth = 0) {
  if (value == null || depth > 9 || typeof value !== "object") return target;
  if (Array.isArray(value)) {
    value.forEach((item) => collectLastActivity(item, target, inheritedPlatform, depth + 1));
    return target;
  }
  const context = [value.sourceName, value.source, value.sourceUrl, value.url, value.platform, value.channel]
    .filter(Boolean).join(" ");
  const platform = socialPlatformFromText(context) || inheritedPlatform;
  Object.entries(value).forEach(([key, item]) => {
    const itemPlatform = socialPlatformFromText(key) || platform;
    if (itemPlatform && LAST_ACTIVITY_KEYS.has(key.toLowerCase().replace(/[^a-z]/g, ""))) {
      const time = parseActivityDate(item);
      // Several keys can describe the same account (post / video / IGTV) — the most
      // recent of them is the one that answers "is this still active?".
      if (time && (!target[itemPlatform] || time > target[itemPlatform])) target[itemPlatform] = time;
    }
    if (item && typeof item === "object") collectLastActivity(item, target, itemPlatform, depth + 1);
  });
  return target;
}

function socialPlatformFromText(value) {
  const text = String(value || "");
  return SOCIAL_PLATFORMS.find((name) => new RegExp(`(^|[^a-z])${name}([^a-z]|$)`, "i").test(text)) || null;
}

function socialMetricDisplay(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  const text = String(value ?? "").trim();
  if (/^-?\d+(?:\.\d+)?$/.test(text)) return Number(text).toLocaleString(undefined, { maximumFractionDigits: 2 });
  return text;
}

function addSocialMetric(target, platform, name, value) {
  const display = socialMetricDisplay(value);
  if (!platform || !name || !display || display.length > 32) return;
  const row = { name, value: display };
  const metrics = target[platform] || (target[platform] = []);
  if (!metrics.some((item) => item.name === row.name && item.value === row.value)) metrics.push(row);
}

function extractMetricsFromText(text, platform, target) {
  if (!platform || typeof text !== "string") return;
  const metricWords = "followers?|following|posts?|likes?|views?|subscribers?|videos?|reviews?|ratings?|comments?|shares?|reactions?|impressions?|connections?|engagement(?:\\s+rate)?";
  const before = new RegExp(`([~+]?[0-9][0-9,.]*(?:\\.[0-9]+)?[KMBkmb]?%?)\\s*(${metricWords})`, "gi");
  const after = new RegExp(`(${metricWords})\\s*(?::|=|of)?\\s*([~+]?[0-9][0-9,.]*(?:\\.[0-9]+)?[KMBkmb]?%?)`, "gi");
  const pushMatches = (pattern, valueIndex, nameIndex) => {
    let match;
    while ((match = pattern.exec(text))) {
      const key = match[nameIndex].toLowerCase().replace(/[^a-z]/g, "").replace(/s$/, "");
      const name = SOCIAL_METRIC_KEYS[key] || label(match[nameIndex]);
      addSocialMetric(target, platform, name, match[valueIndex]);
    }
  };
  pushMatches(before, 1, 2);
  pushMatches(after, 2, 1);
}

function collectSocialMetrics(value, target = {}, inheritedPlatform = null, depth = 0) {
  if (value == null || depth > 9) return target;
  if (typeof value === "string") {
    // Text-scraped metrics disabled: prose yields duplicates of API values in a
    // different format ("1.4K" beside "1,465"), and occasionally a number that is not
    // the metric at all — rendered identically to a verified figure, with no way for a
    // reader to tell them apart.
    // extractMetricsFromText(value, socialPlatformFromText(value) || inheritedPlatform, target);
    return target;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectSocialMetrics(item, target, inheritedPlatform, depth + 1));
    return target;
  }
  if (typeof value !== "object") return target;
  const context = [value.sourceName, value.source, value.sourceUrl, value.url, value.platform, value.channel].filter(Boolean).join(" ");
  const platform = socialPlatformFromText(context) || inheritedPlatform;
  Object.entries(value).forEach(([key, item]) => {
    const itemPlatform = socialPlatformFromText(key) || platform;
    const metricName = SOCIAL_METRIC_KEYS[key.toLowerCase().replace(/[^a-z]/g, "")];
    // Per-video keys arrive as playCount_1/_2/_3 and all collapse to the same metric
    // name, which would render two or three unlabelled "Views" tiles. Only the newest
    // video (slot 1) is shown, so each metric appears once.
    const isOlderVideoSlot = /_[2-9]\d*$/.test(key);
    if (itemPlatform && metricName && !isOlderVideoSlot && ["string", "number"].includes(typeof item)) addSocialMetric(target, itemPlatform, metricName, item);
    // if (typeof item === "string") extractMetricsFromText(item, itemPlatform, target);
    if (item && typeof item === "object") collectSocialMetrics(item, target, itemPlatform, depth + 1);
  });
  return target;
}

export function summarizeDigitalSocialEvidence(value = {}, reportText = "") {
  const data = value?.data || {};
  const orchestrator = data.orchestrator || {};
  const orchestratorData = orchestrator.data || {};
  const company = orchestratorData.company || {};
  const sourceResults = Array.isArray(orchestratorData.results) ? orchestratorData.results : [];
  const sourceStatuses = [data.stored, data.orchestrator, data.webResearch]
    .filter((item) => item && item.status);
  const successSources = sourceResults
    .filter((item) => item?.status === "success" && item?.data)
    .map((item) => label(item.source || "Source"));
  const unavailableSourceCount = sourceResults.filter((item) =>
    ["failed", "error", "unavailable"].includes(String(item?.status || "").toLowerCase()),
  ).length;
  const urls = [...new Set(collectEvidenceUrls(value))];
  const socialMetrics = collectSocialMetrics(value);
  // Disabled with the other text-scraping call sites: the underwriting narrative
  // rounds figures ("1.4K likes"), which surfaced as a second tile alongside the exact
  // API value. Re-enable only if metrics are labelled by provenance.
  // String(reportText || "").split(/\r?\n/).forEach((line) => {
  //   const platform = socialPlatformFromText(line);
  //   if (platform) extractMetricsFromText(line, platform, socialMetrics);
  // });
  // Surface recency alongside audience size. Rendered as a tile like any other metric
  // so no change to the card layout is needed.
  Object.entries(collectLastActivity(value)).forEach(([platform, time]) => {
    addSocialMetric(socialMetrics, platform, "Last post", formatLastActivity(time));
  });
  const channelRules = [
    ["Website", (url) => url === validHttpUrl(company.companyWebsite) || url === validHttpUrl(company.website)],
    ["Instagram", (url) => /(^|\.)instagram\.com$/i.test(new URL(url).hostname)],
    ["Facebook", (url) => /(^|\.)facebook\.com$/i.test(new URL(url).hostname)],
    ["YouTube", (url) => /(^|\.)youtube\.com$/i.test(new URL(url).hostname)],
    ["TikTok", (url) => /(^|\.)tiktok\.com$/i.test(new URL(url).hostname)],
    ["LinkedIn", (url) => /(^|\.)linkedin\.com$/i.test(new URL(url).hostname) && /\/(company|showcase)\//i.test(new URL(url).pathname)],
  ];
  const channels = [];
  channelRules.forEach(([name, matches]) => {
    const url = urls.find(matches);
    const metrics = socialMetrics[name] || [];
    if (url || metrics.length) channels.push({ name, url: url || null, metrics });
  });
  const findings = collectEvidenceFindings(data.webResearch?.data || {})
    .filter((item, index, rows) => rows.findIndex((candidate) => candidate.finding === item.finding) === index)
    .slice(0, 4);
  const availableSourceCount = sourceStatuses.filter((item) => item?.status === "available").length;
  const companyName = company.name || company.companyName || null;
  const location = [company.address, company.country].filter(Boolean).join(", ") || null;
  return {
    status: value?.status || (availableSourceCount ? "available" : null),
    companyName,
    location,
    lastChecked: orchestratorData.fetchedAt || value?.retrievedAt || orchestrator?.retrievedAt || null,
    availableSourceCount,
    totalSourceCount: sourceStatuses.length,
    successSources: [...new Set(successSources)],
    unavailableSourceCount,
    channels,
    findings,
  };
}

function DigitalSocialEvidenceSummary({ data, reportText }) {
  const summary = summarizeDigitalSocialEvidence(data, reportText);
  const hasContent = summary.status || summary.companyName || summary.channels.length || summary.findings.length;
  if (!hasContent) return null;
  const checkedAt = summary.lastChecked && !Number.isNaN(new Date(summary.lastChecked).getTime())
    ? new Date(summary.lastChecked).toLocaleString()
    : null;
  return (
    <Section
      title="Digital & social evidence"
      subtitle="Concise business-only summary of the usable digital evidence returned for this report."
    >
      <FieldGrid data={{
        status: summary.status === "available" ? "Available" : label(summary.status),
        business: summary.companyName,
        location: summary.location,
        evidenceSources: summary.totalSourceCount
          ? `${summary.availableSourceCount} of ${summary.totalSourceCount} available`
          : null,
        lastChecked: checkedAt,
      }} />
      {summary.channels.length > 0 && (
        <Box sx={{ mt: 1.75 }}>
          <Typography sx={{ color: C.muted, fontSize: 11, fontWeight: 900, textTransform: "uppercase", mb: 0.75 }}>Business channels located</Typography>
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
            {summary.channels.map((channel) => (
              channel.url ? <Chip key={`${channel.name}-${channel.url}`} component="a" clickable href={channel.url} target="_blank" rel="noreferrer" label={channel.name} variant="outlined" sx={{ color: C.blue, fontWeight: 800 }} />
                : <Chip key={channel.name} label={channel.name} variant="outlined" sx={{ color: C.blue, fontWeight: 800 }} />
            ))}
          </Box>
        </Box>
      )}
      {summary.channels.some((channel) => channel.metrics?.length) && (
        <Box sx={{ mt: 1.75 }}>
          <Typography sx={{ color: C.muted, fontSize: 11, fontWeight: 900, textTransform: "uppercase", mb: 0.75 }}>Social audience & engagement</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 1 }}>
            {summary.channels.filter((channel) => channel.metrics?.length).map((channel) => (
              <Paper key={`${channel.name}-metrics`} variant="outlined" sx={{ p: 1.4, borderColor: C.line, borderRadius: 2 }}>
                <Typography component={channel.url ? "a" : "p"} href={channel.url || undefined} target={channel.url ? "_blank" : undefined} rel={channel.url ? "noreferrer" : undefined} sx={{ color: channel.url ? C.blue : C.ink, fontSize: 13, fontWeight: 850, textDecoration: "none" }}>{channel.name}</Typography>
                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 1 }}>
                  {channel.metrics.map((metric, index) => (
                    <Box key={`${metric.name}-${metric.value}-${index}`} sx={{ minWidth: 82, px: 1, py: 0.75, bgcolor: C.bg, borderRadius: 1.5 }}>
                      <Typography sx={{ color: C.ink, fontSize: 14, fontWeight: 900 }}>{metric.value}</Typography>
                      <Typography sx={{ color: C.muted, fontSize: 9.5, fontWeight: 800, textTransform: "uppercase" }}>{metric.name}</Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>
      )}
      {summary.findings.length > 0 && (
        <Box sx={{ mt: 1.75, display: "grid", gap: 0.75 }}>
          <Typography sx={{ color: C.muted, fontSize: 11, fontWeight: 900, textTransform: "uppercase" }}>Key findings</Typography>
          {summary.findings.map((item, index) => (
            <Typography key={`${item.finding}-${index}`} sx={{ color: C.ink, fontSize: 13.5, lineHeight: 1.55 }}>
              • {item.finding}{item.sourceName ? ` — ${item.sourceName}` : ""}
            </Typography>
          ))}
        </Box>
      )}
      {summary.successSources.length > 0 && (
        <Typography sx={{ color: C.muted, fontSize: 12.5, mt: 1.5 }}>
          Returned data: {summary.successSources.join(", ")}.
        </Typography>
      )}
      {summary.unavailableSourceCount > 0 && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          {summary.unavailableSourceCount} external {summary.unavailableSourceCount === 1 ? "source was" : "sources were"} unavailable in this run and treated as missing evidence.
        </Alert>
      )}
    </Section>
  );
}

function IdentityVerificationPanel({ entity, assessment }) {
  const confidence = Math.round((entity.match?.confidence || 0) * 100);
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 1.75 }}>
      <Section
        title="Verified business profile"
        subtitle="Best available identity assembled from Wix and external evidence."
      >
        <DataRows data={entity.identity} />
      </Section>
      <Section
        title="Verification summary"
        subtitle="Confidence by verification layer."
      >
        <ChecklistRow title="Company name" detail="Merchant record name available" status="Verified" ready />
        <ChecklistRow title="Website" detail={entity.identity?.website ? "Public domain supplied" : "No website supplied"} status={entity.identity?.website ? "Verified" : "Missing"} ready={Boolean(entity.identity?.website)} />
        {/* Separate row: "a domain was supplied" and "that domain actually loads" are
            different facts, so the reachability verdict gets its own line rather than
            overwriting the supplied/missing check above. */}
        {entity.identity?.website ? (
          <ChecklistRow
            title="Website reachability"
            // detail={entity.websiteReachability?.reason || "Not checked yet"}
            status={entity.websiteReachability ? "" : "Not checked"}
            ready={entity.websiteReachability?.availability === "REACHABLE_OR_UNKNOWN"}
            action={entity.websiteReachability ? (
              <WebsiteReachabilityBadge
                verdict={entity.websiteReachability}
                website={entity.identity.website}
              />
            ) : null}
          />
        ) : null}
        <ChecklistRow title="Entity reconciliation" detail={`${confidence}% overall match · ${assessment.coverage || 0}% evidence coverage`} status={confidence >= 80 ? "Verified" : "Review"} ready={confidence >= 80} />
      </Section>
    </Box>
  );
}

function applicationRecord(value, index) {
  if (value && typeof value === "object") {
    return {
      ...value,
      id: value.id || value.applicationId || value.externalId || `Application ${index + 1}`,
      status: value.status || value.outcome || value.decision || "Recorded",
    };
  }
  return { id: String(value), status: "Recorded" };
}

function ApplicationsOutcomesPanel({ entity }) {
  const records = (entity.applicationHistory || entity.applications || []).map(applicationRecord);
  const [selectedId, setSelectedId] = useState(records[0]?.id || "");
  useEffect(() => {
    if (!records.some((record) => record.id === selectedId)) setSelectedId(records[0]?.id || "");
  }, [records, selectedId]);
  const selected = records.find((record) => record.id === selectedId) || records[0] || null;
  const outcomes = entity.outcomes || {};
  const funded = Number(outcomes.funded || 0);
  const notFunded = Number(outcomes.notFunded || 0);
  const events = selected?.events || selected?.history || [];
  const statusColor = (status) => {
    const normalized = String(status || "").toLowerCase();
    if (/funded|approved|performing|complete/.test(normalized)) return C.green;
    if (/declin|default|late|adverse/.test(normalized)) return C.red;
    if (/review|pending|requested/.test(normalized)) return C.amber;
    return C.muted;
  };
  return (
    <Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, 1fr)" }, gap: 1.25, mb: 2 }}>
        <Metric title="Applications" value={(entity.applicationCount || records.length || 0).toLocaleString()} />
        <Metric title="Funded" value={funded.toLocaleString()} />
        <Metric title="Not funded" value={notFunded.toLocaleString()} />
      </Box>
      {!records.length ? (
        <Section title="Application history" subtitle="Applications, decisions and observed performance will appear here as one auditable record.">
          <Box sx={{ py: 4, px: 2, textAlign: "center", bgcolor: "#f8fafc", borderRadius: 2.5 }}>
            <Typography sx={{ color: C.ink, fontWeight: 900 }}>No application history linked</Typography>
            <Typography sx={{ color: C.muted, fontSize: 13, mt: 0.5 }}>
              Application identifiers and outcomes have not been supplied for this company.
            </Typography>
          </Box>
        </Section>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "300px minmax(0, 1fr)" }, gap: 2, alignItems: "start" }}>
          <Paper variant="outlined" sx={{ borderColor: C.line, borderRadius: 3, overflow: "hidden" }}>
            <Box sx={{ p: 1.5, borderBottom: `1px solid ${C.line}` }}>
              <Typography sx={{ color: C.ink, fontWeight: 900 }}>Application history</Typography>
              <Typography sx={{ color: C.muted, fontSize: 12 }}>Select a record to inspect its outcome.</Typography>
            </Box>
            {records.map((record) => (
              <Button
                key={record.id}
                onClick={() => setSelectedId(record.id)}
                aria-pressed={record.id === selected?.id}
                sx={{
                  width: "100%",
                  display: "block",
                  textAlign: "left",
                  textTransform: "none",
                  p: 1.5,
                  borderRadius: 0,
                  borderBottom: `1px solid ${C.line}`,
                  bgcolor: record.id === selected?.id ? "#eef4fb" : "#fff",
                  color: C.ink,
                  "&:last-child": { borderBottom: 0 },
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "center" }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 900 }}>{record.id}</Typography>
                  <Chip size="small" label={label(record.status)} sx={{ color: statusColor(record.status), bgcolor: `${statusColor(record.status)}12`, fontWeight: 800 }} />
                </Box>
                <Typography sx={{ color: C.muted, fontSize: 11.5, mt: 0.5 }}>
                  {[record.createdAt && new Date(record.createdAt).toLocaleDateString(), record.requestedAmount != null && money(record.requestedAmount)].filter(Boolean).join(" · ") || "Identifier only"}
                </Typography>
              </Button>
            ))}
          </Paper>
          <Section title={`${selected.id} · ${label(selected.status)}`} subtitle="Application terms, decision and outcome evidence available for this record.">
            <FieldGrid
              data={{
                requestedAmount: selected.requestedAmount,
                fundedAmount: selected.fundedAmount,
                decision: selected.decision,
                status: selected.status,
                factorRate: selected.factorRate,
                holdbackPercent: selected.holdbackPercent,
                submittedAt: selected.createdAt || selected.submittedAt,
                decidedAt: selected.decidedAt,
                fundedAt: selected.fundedAt,
                outcome: selected.outcome,
              }}
              empty="Only the application identifier was supplied."
            />
            {!!events.length && (
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ color: C.ink, fontWeight: 900, mb: 0.5 }}>Activity timeline</Typography>
                {events.map((event, index) => (
                  <Box key={event.id || `${event.type}-${index}`} sx={{ py: 1, borderBottom: `1px solid ${C.line}` }}>
                    <Typography sx={{ color: C.ink, fontWeight: 800 }}>{label(event.type || event.status || "Activity")}</Typography>
                    <Typography sx={{ color: C.muted, fontSize: 12 }}>
                      {[event.actor, event.createdAt && new Date(event.createdAt).toLocaleString()].filter(Boolean).join(" · ")}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Section>
        </Box>
      )}
      {!!Object.keys(outcomes.decisions || {}).length && (
        <Section title="Historical decision outcomes" subtitle="Complete outcome counts supplied for this company.">
          <FieldGrid data={outcomes.decisions} />
        </Section>
      )}
    </Box>
  );
}

function SignalStatsTable({ stats }) {
  const entries = Object.entries(stats || {});
  if (!entries.length) return <Typography sx={{ color: C.muted }}>No signal history was supplied.</Typography>;
  return (
    <Box>
      {entries.map(([key, raw]) => {
        const value = raw && typeof raw === "object" ? raw : { value: raw };
        const count = value.count ?? (value.value != null ? 1 : null);
        const average = value.average ?? value.avg ?? (value.sum != null && Number(count) ? Number(value.sum) / Number(count) : value.value);
        const min = Number(value.min);
        const max = Number(value.max);
        const numericAverage = Number(average);
        const meter = Number.isFinite(numericAverage) && Number.isFinite(min) && Number.isFinite(max) && max > min ? Math.max(5, Math.min(100, ((numericAverage - min) / (max - min)) * 100)) : 72;
        return (
          <Box key={key} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "170px minmax(120px, 1fr) 150px" }, gap: 1.5, alignItems: "center", py: 1.2, borderBottom: `1px solid ${C.line}`, "&:last-child": { borderBottom: 0 } }}>
            <Typography sx={{ color: C.muted, fontSize: 12 }}>{label(key)}</Typography>
            <Box sx={{ height: 7, borderRadius: 99, bgcolor: "#e7edf4", overflow: "hidden" }}><Box sx={{ width: `${meter}%`, height: "100%", borderRadius: 99, bgcolor: "#5268ad" }} /></Box>
            <Typography sx={{ color: C.ink, fontSize: 11.5, fontWeight: 750, textAlign: { sm: "right" } }}>{valueText(average)}{count != null ? ` · ${count} observed` : ""}</Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function PlatformKpis({ entity }) {
  const signals = entity.signals || {};
  const transactions = signals.transactions ?? signals.transactionCount ?? signals.totalTransactions ?? entity.signalStats?.transactions?.count;
  const monthlyVolume = signals.averageMonthlyVolume ?? signals.avgMonthlyVolume ?? signals.monthlyVolume ?? signals.averageMonthlyRevenue;
  const volatility = signals.turnoverVolatility ?? signals.revenueVolatility ?? signals.volumeVolatility;
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.25, mb: 1.75 }}>
      <Metric title="Transactions" value={transactions == null ? "—" : valueText(transactions)} />
      <Metric title="Avg. monthly volume" value={monthlyVolume == null ? "—" : money(monthlyVolume)} />
      <Metric title="Turnover volatility" value={volatility == null ? "—" : `${Number(volatility) <= 1 ? (Number(volatility) * 100).toFixed(1) : Number(volatility).toFixed(1)}%`} />
    </Box>
  );
}

function MonthlyPerformanceTable({ rows = [] }) {
  if (!rows.length)
    return <Typography sx={{ color: C.muted }}>No month-by-month history was supplied.</Typography>;
  const columns = [
    ["month", "Month"],
    ["paymentVolume", "Payment volume"],
    ["transactions", "Transactions"],
    ["disputes", "Disputes"],
    ["logins", "Logins"],
    ["productViews", "Product views"],
  ];
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 720, "th,td": { p: 1.15, borderBottom: `1px solid ${C.line}`, textAlign: "right", fontSize: 13 }, "th:first-of-type,td:first-of-type": { textAlign: "left" }, th: { color: C.muted, fontSize: 11, textTransform: "uppercase" } }}>
        <thead><tr>{columns.map(([, title]) => <th key={title}>{title}</th>)}</tr></thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.month}>
              {columns.map(([key]) => <td key={key}>{key === "paymentVolume" && row[key] != null ? money(row[key]) : valueText(row[key])}</td>)}
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}

function LockedCommercialTerms({ terms }) {
  if (!terms?.locked || (terms.factorRate == null && terms.remittanceRate == null)) return null;
  return (
    <Section title="Locked commercial terms" subtitle="Client-supplied inputs used by underwriting. These values cannot be changed by the recommendation engine.">
      <Alert severity="info" sx={{ mb: 1.5 }}>Underwriting may adjust the recommended amount or term for affordability, but it must preserve these rates exactly.</Alert>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1.25 }}>
        <Metric title="Factor rate" value={terms.factorRate == null ? "—" : Number(terms.factorRate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })} detail="Locked input" />
        <Metric title="Remittance rate" value={terms.remittanceRate == null ? "—" : `${Number(terms.remittanceRate).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`} detail="Locked input" />
      </Box>
    </Section>
  );
}

function BankStatementEvidence({ statements = [], summary = null }) {
  if (!statements.length && !summary) return null;
  return (
    <Section title="Bank statement analysis" subtitle="Client-uploaded statements normalized into cash-flow evidence used by the underwriting recommendation.">
      {summary?.requiresManualReview && <Alert severity="warning" sx={{ mb: 1.5 }}>The statement analysis contains activity or document-quality findings that require human review.</Alert>}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, minmax(0, 1fr))" }, gap: 1.25, mb: 1.5 }}>
        <Metric title="Statements" value={summary?.statementCount ?? statements.length} detail={(summary?.monthsCovered || []).join(", ") || "Analyzed PDFs"} />
        <Metric title="Avg. monthly deposits" value={summary?.averageMonthlyDeposits == null ? "—" : money(summary.averageMonthlyDeposits)} detail="Across covered months" />
        <Metric title="Avg. daily balance" value={summary?.averageDailyBalance == null ? "—" : money(summary.averageDailyBalance)} detail="When reported" />
        <Metric title="Existing MCA remittances" value={summary?.mcaRemittanceAmount == null ? "—" : money(summary.mcaRemittanceAmount)} detail={`${summary?.mcaRemittanceCount || 0} observed payment(s)`} />
      </Box>
      <FieldGrid data={{
        totalDeposits: summary?.totalDeposits == null ? null : money(summary.totalDeposits),
        totalDebits: summary?.totalDebits == null ? null : money(summary.totalDebits),
        lowestBalance: summary?.lowestBalance == null ? null : money(summary.lowestBalance),
        negativeBalanceDays: summary?.negativeBalanceDays,
        lowBalanceDays: summary?.lowBalanceDays,
        nsfItems: summary?.nsfCount,
        returnedItems: summary?.returnedItemCount,
        overdrafts: summary?.overdraftCount,
        chargebacks: summary?.chargebackAmount == null ? null : money(summary.chargebackAmount),
        fees: summary?.feesAmount == null ? null : money(summary.feesAmount),
      }} />
      {!!statements.length && (
        <Box sx={{ display: "grid", gap: 1, mt: 1.5 }}>
          {statements.map((statement) => (
            <Paper key={statement.id || statement.fileName} variant="outlined" sx={{ p: 1.25, borderColor: C.line, borderRadius: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                <Typography sx={{ color: C.ink, fontWeight: 850 }}>{statement.fileName}</Typography>
                <Chip size="small" label={`${label(statement.analysis?.tamperRisk || "unknown")} document risk`} color={statement.analysis?.tamperRisk === "low" ? "success" : "warning"} variant="outlined" />
              </Box>
              <Typography sx={{ color: C.muted, fontSize: 12, mt: .5 }}>
                {[statement.analysis?.bankName, statement.analysis?.statementMonth, statement.analysis?.summary].filter(Boolean).join(" · ")}
              </Typography>
            </Paper>
          ))}
        </Box>
      )}
    </Section>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, mb: 1.75, borderColor: C.line, borderRadius: 2.5, boxShadow: "0 8px 24px rgba(17, 50, 83, 0.025)" }}
    >
      <Typography sx={{ color: C.ink, fontSize: 14, fontWeight: 800 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ color: C.muted, fontSize: 11.5, mb: 1.5 }}>
          {subtitle}
        </Typography>
      )}
      {!subtitle && <Box sx={{ mb: 1.5 }} />}
      {children}
    </Paper>
  );
}

function RecommendationSummary({ entity }) {
  const summary = buildRecommendationPresentation(entity);
  const score = Number(summary.creditScore || 0);
  return (
    <Section
      title="Underwriting recommendation"
      subtitle="Modeled result, supporting rationale, and current commercial terms."
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, pb: 1.75, borderBottom: `1px solid ${C.line}` }}>
        <Box sx={{ width: 76, height: 76, flex: "0 0 auto", borderRadius: "50%", display: "grid", placeItems: "center", background: `conic-gradient(${summary.color} 0 ${Math.max(0, Math.min(100, score))}%, #e7edf4 ${Math.max(0, Math.min(100, score))}% 100%)`, position: "relative", "&:after": { content: '""', position: "absolute", inset: 7, borderRadius: "50%", bgcolor: "#fff" } }}>
          <Typography sx={{ position: "relative", zIndex: 1, color: C.ink, fontSize: 21, fontWeight: 850 }}>{summary.creditScore ?? "—"}</Typography>
        </Box>
        <Box>
          <Typography sx={{ color: summary.color, fontSize: 18, fontWeight: 850 }}>{summary.label}</Typography>
          <Typography sx={{ color: C.muted, fontSize: 12 }}>{summary.amount == null ? "No commercial offer generated" : `${money(summary.amount)} recommended`}</Typography>
          {summary.chosenTier && <Typography sx={{ color: C.muted, fontSize: 11, mt: 0.25 }}>Tier · {summary.chosenTier}</Typography>}
        </Box>
      </Box>
      {summary.decision === "approved_with_conditions" && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: "#f5c46b", bgcolor: "#fffbeb" }}>
          <Typography sx={{ color: C.ink, fontWeight: 900, mb: 1 }}>Approval conditions</Typography>
          {summary.conditions.length ? (
            <Box component="ul" sx={{ my: 0, pl: 2.5, color: C.ink }}>
              {summary.conditions.map((condition) => <li key={condition}>{condition}</li>)}
            </Box>
          ) : (
            <Typography sx={{ color: C.muted }}>Review the report stipulations before approval.</Typography>
          )}
        </Paper>
      )}
      <Typography sx={{ color: "#41556b", fontSize: 12.5, lineHeight: 1.65, pt: 1.5 }}>{summary.explanation}</Typography>
      <Box sx={{ mt: 1.5, display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", borderTop: `1px solid ${C.line}` }}>
        <SummaryCell title="Amount" value={summary.amount == null ? "No offer" : money(summary.amount)} />
        <SummaryCell title="Holdback" value={summary.holdbackPercent == null ? "—" : `${Number(summary.holdbackPercent).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`} />
        <SummaryCell title="Score" value={summary.creditScore ?? "—"} detail="of 100" />
      </Box>
    </Section>
  );
}

function ExplanationValue({ label: title, value, detail }) {
  return (
    <Box sx={{ minWidth: 0, height: "100%", display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "flex-start", textAlign: "left" }}>
      <Typography sx={{ color: C.muted, fontSize: 10.5, lineHeight: 1.35, fontWeight: 800 }}>{title}</Typography>
      <Typography sx={{ color: C.ink, mt: 0.25, fontSize: 15, lineHeight: 1.35, fontWeight: 900, overflowWrap: "anywhere" }}>{value}</Typography>
      {detail && <Typography sx={{ color: C.muted, mt: 0.35, fontSize: 10.5, lineHeight: 1.5 }}>{detail}</Typography>}
    </Box>
  );
}

function ScoreExplainability({ entity }) {
  const explanation = buildScoreExplainability(entity);
  const score = explanation.displayedScore ?? explanation.modelScore;
  return (
    <Section title="Why this score" subtitle="Auditable calculation from the evidence available to the scoring model.">
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 1.5 }}>
        <Box>
          <Typography sx={{ color: C.muted, fontSize: 10.5, fontWeight: 850, textTransform: "uppercase" }}>Displayed score</Typography>
          <Typography sx={{ color: C.ink, fontSize: 28, lineHeight: 1.15, fontWeight: 950 }}>{score ?? "—"}<Box component="span" sx={{ color: C.muted, fontSize: 13, fontWeight: 700 }}> / 100</Box></Typography>
        </Box>
        <Chip size="small" color={explanation.hasFullUnderwriting ? "primary" : "default"} variant="outlined" label={explanation.hasFullUnderwriting ? "Full underwriting" : `${explanation.coverage}% evidence coverage`} sx={{ fontWeight: 750 }} />
      </Box>
      {explanation.hasFullUnderwriting && (
        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 1.5, minWidth: 0, textAlign: "left", borderColor: "#cfe0ff", borderRadius: 2, bgcolor: "#f7faff" }}>
          <Typography sx={{ color: C.blue, fontSize: 10.5, fontWeight: 900, textTransform: "uppercase" }}>Primary explanation · Full underwriting Section 10</Typography>
          <UnderwritingMarkdownBlock
            testId="score-underwriting-markdown"
            markdown={explanation.fullUnderwritingSection?.markdown || explanation.fullUnderwritingExplanation}
            fallback="The full underwriting output supplied the displayed score, but did not include a separate Section 10 score bridge."
          />
        </Paper>
      )}
      {explanation.evidence.length ? (
        <>
          <Box component="details" open={!explanation.hasFullUnderwriting} sx={{ border: `1px solid ${C.line}`, borderRadius: 2, bgcolor: "#f8fafc", overflow: "hidden", "& > summary": { cursor: "pointer", color: C.ink, px: 1.5, py: 1.15, fontSize: 11.5, fontWeight: 850 } }}>
            <Box component="summary">{explanation.hasFullUnderwriting ? "Earlier evidence-model baseline" : "Evidence-model calculation"} · {explanation.coverage}% coverage</Box>
            <Box sx={{ px: 1.5, pb: 1.5 }}>
              <Typography sx={{ color: C.ink, fontSize: 12.5, fontWeight: 800 }}>
                ({explanation.weightedPoints.toFixed(2)} weighted points ÷ {explanation.observedWeight.toFixed(0)} observed weight) × 100 = {explanation.evidenceScore.toFixed(1)}
              </Typography>
              <Typography sx={{ color: C.muted, mt: 0.35, mb: 1.25, fontSize: 10.5 }}>
                Missing features are excluded from the denominator; coverage shows how much of the configured model was actually observed.
              </Typography>
              <Box sx={{ display: "grid", gap: 1 }}>
                {explanation.evidence.slice().sort((a, b) => b.weight - a.weight).map((item) => (
                  <Box key={item.feature} sx={{ display: "grid", gridTemplateColumns: "minmax(120px, 1fr) 72px 82px", gap: 1, alignItems: "center" }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ color: C.ink, fontSize: 11.5, fontWeight: 800 }}>{label(item.feature)}</Typography>
                      <Box sx={{ mt: 0.45, height: 4, borderRadius: 99, bgcolor: "#e7edf4", overflow: "hidden" }}><Box sx={{ width: `${Math.max(0, Math.min(100, item.normalizedValue * 100))}%`, height: "100%", bgcolor: C.blue }} /></Box>
                    </Box>
                    <Typography sx={{ color: C.muted, fontSize: 10.5 }}>Weight {item.weight}</Typography>
                    <Typography sx={{ color: C.ink, fontSize: 10.5, fontWeight: 850, textAlign: "right" }}>{item.contribution.toFixed(2)} weighted</Typography>
                  </Box>
                ))}
              </Box>
              {explanation.ml && (
                <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2, py: 0.25 }}>
                  The evidence score was blended with an active ML prediction at {Math.round(Number(explanation.ml.blendWeight || 0) * 100)}% weight to produce the model score of {explanation.modelScore}.
                </Alert>
              )}
            </Box>
          </Box>
          {explanation.fullUnderwritingOverride && (
            <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2, py: 0.25 }}>
              Full underwriting set the displayed score to {explanation.displayedScore}; the evidence model baseline was {explanation.modelScore}. See the score bridge in the full report for its adjustments.
            </Alert>
          )}
        </>
      ) : (
        <Typography sx={{ color: C.muted, fontSize: 12.5 }}>No scored evidence was available, so a score calculation cannot be shown.</Typography>
      )}
    </Section>
  );
}

function LimitExplainability({ entity }) {
  const explanation = buildLimitExplainability(entity);
  return (
    <Section title="How the recommended limit was determined" subtitle="Decision gate, sizing inputs, and the source of the final recommendation.">
      {explanation.hasFullUnderwriting && (
        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 1.5, minWidth: 0, textAlign: "left", borderColor: "#cfe0ff", borderRadius: 2, bgcolor: "#f7faff" }}>
          <Typography sx={{ color: C.blue, fontSize: 10.5, fontWeight: 900, textTransform: "uppercase" }}>Primary sizing source · Full underwriting Section 11</Typography>
          <UnderwritingMarkdownBlock
            testId="limit-underwriting-markdown"
            markdown={explanation.sectionElevenDetail?.markdown || explanation.explanation}
            fallback="The full underwriting summary supplied the recommendation, but did not include a separate Section 11 sizing table."
          />
        </Paper>
      )}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.5, pb: 1.5, borderBottom: `1px solid ${C.line}` }}>
        <ExplanationValue label="Requested amount" value={explanation.requestedAmount == null ? "Not supplied" : money(explanation.requestedAmount)} />
        <ExplanationValue label="Avg. monthly volume" value={explanation.monthlyVolume == null ? "Not supplied" : money(explanation.monthlyVolume)} />
        <ExplanationValue label="Risk band" value={explanation.riskBand ? label(explanation.riskBand) : "Not assigned"} />
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "160px minmax(0, 1fr)" }, gap: 1.5, py: 1.5, borderBottom: `1px solid ${C.line}` }}>
        <ExplanationValue label="Decision gate" value={explanation.decisionLabel} detail={explanation.decision === "not_approved" ? "A declined decision suppresses commercial terms." : "The recommendation passed the decision gate."} />
        <ExplanationValue label="Recommendation source" value={explanation.amountSource} detail={explanation.chosenTier ? `Selected tier: ${explanation.chosenTier}` : null} />
      </Box>
      <Box sx={{ pt: 1.5 }}>
        <Typography sx={{ color: C.muted, fontSize: 10.5, fontWeight: 850, textTransform: "uppercase" }}>Result</Typography>
        <Typography sx={{ color: explanation.decisionColor, fontSize: 23, fontWeight: 950 }}>{explanation.amount == null ? "No limit recommended" : money(explanation.amount)}</Typography>
        {explanation.holdbackPercent != null && <Typography sx={{ color: C.muted, fontSize: 11.5 }}>Recommended holdback: {Number(explanation.holdbackPercent).toLocaleString(undefined, { maximumFractionDigits: 2 })}%</Typography>}
        <Typography sx={{ color: "#41556b", mt: 1, fontSize: 12.5, lineHeight: 1.65 }}>{explanation.explanation}</Typography>
      </Box>
    </Section>
  );
}

function RecommendationExplainability({ entity }) {
  const explanation = buildRecommendationExplainability(entity);
  if (!explanation.hasFullUnderwriting) return null;
  const rationale = [
    ["Decision rationale", explanation.decisionRationale],
    ["Score rationale", explanation.scoreRationale],
    ["Offer rationale", explanation.limitRationale],
  ].filter(([, value]) => value);
  return (
    <Section title="Why this recommendation" subtitle="Evidence-specific reasoning supplied by the completed full underwriting analysis.">
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(3, minmax(0, 1fr))" }, gap: 1.25 }}>
        {rationale.map(([title, value]) => (
          <Paper key={title} variant="outlined" sx={{ p: 1.5, height: "100%", textAlign: "left", borderColor: "#cfe0ff", borderRadius: 2, bgcolor: "#f7faff" }}>
            <Typography sx={{ color: C.blue, fontSize: 10.5, fontWeight: 900, textTransform: "uppercase" }}>{title}</Typography>
            <Typography sx={{ color: "#29445f", mt: 0.65, fontSize: 12.5, lineHeight: 1.7, overflowWrap: "anywhere" }}>{value}</Typography>
          </Paper>
        ))}
      </Box>
      {(explanation.keyDrivers.length > 0 || explanation.whatWouldChangeRecommendation.length > 0) && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 2, mt: 1.75 }}>
          {explanation.keyDrivers.length > 0 && (
            <Box>
              <Typography sx={{ color: C.ink, fontSize: 12, fontWeight: 900 }}>Most influential evidence</Typography>
              <Box component="ul" sx={{ color: "#41556b", mt: 0.75, mb: 0, pl: 2.4, fontSize: 12.5, lineHeight: 1.65 }}>
                {explanation.keyDrivers.map((item) => <li key={item}>{item}</li>)}
              </Box>
            </Box>
          )}
          {explanation.whatWouldChangeRecommendation.length > 0 && (
            <Box>
              <Typography sx={{ color: C.ink, fontSize: 12, fontWeight: 900 }}>What could change the recommendation</Typography>
              <Box component="ul" sx={{ color: "#41556b", mt: 0.75, mb: 0, pl: 2.4, fontSize: 12.5, lineHeight: 1.65 }}>
                {explanation.whatWouldChangeRecommendation.map((item) => <li key={item}>{item}</li>)}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Section>
  );
}

function DecisionChecklist({ entity }) {
  const assessment = entity.assessment || {};
  const match = Math.round((entity.match?.confidence || 0) * 100);
  const missing = assessment.missingFeatures || [];
  return (
    <Section title="Decision checklist" subtitle={`${missing.length + (match < 80 ? 1 : 0)} items block completion.`}>
      <ChecklistRow title="Risk score reviewed" detail={`${assessment.score ?? "—"} · ${label(assessment.recommendation?.riskBand || "unscored")} risk band`} status="Ready" ready />
      <ChecklistRow title="Identity evidence" detail={`${match}% match confidence`} status={match >= 80 ? "Ready" : "Review"} ready={match >= 80} />
      <ChecklistRow title="Cash-flow evidence" detail={missing.length ? `${missing.length} configured items missing` : "Required evidence supplied"} status={missing.length ? "Missing" : "Ready"} ready={!missing.length} />
    </Section>
  );
}

const UNDERWRITING_PROGRESS_STEPS = [
  { key: "registration_ucc", label: "Identity & filings" },
  { key: "dnb", label: "Financial records" },
  { key: "digital_social", label: "Digital & public signals" },
  { key: "underwriting_llm", label: "Recommendation" },
  { key: "arc_score", label: "Business health" },
];

export function buildUnderwritingProgressPresentation(stageInput, progressInput) {
  const stage = String(stageInput || "queued").toLowerCase();
  const progress = Math.max(0, Math.min(100, Number(progressInput) || 0));
  const presentations = {
    queued: {
      title: "Underwriting is queued",
      detail: "Waiting for an available underwriting worker. Evidence collection has not started yet.",
      activeStep: -1,
      phase: "Waiting to start",
    },
    starting: {
      title: "Starting the underwriting review",
      detail: "Preparing the company record and the evidence sources required for this run.",
      activeStep: 0,
      phase: "Starting",
    },
    registration_ucc: {
      title: "Checking identity, registration, and lien records",
      detail: "Verifying available business-registration details and searching official UCC and filing sources.",
      activeStep: 0,
      phase: "Identity & filings",
    },
    dnb: {
      title: "Retrieving company and financial records",
      detail: "Collecting available company profile, financial reporting, and strength indicators for the assessment.",
      activeStep: 1,
      phase: "Financial records",
    },
    digital_social: {
      title: "Reviewing digital presence and public signals",
      detail: "Reviewing the company website, digital footprint, and relevant public business signals.",
      activeStep: 2,
      phase: "Digital & public signals",
    },
    underwriting_llm: {
      title: "Generating the recommendation and report",
      detail: "Combining the collected evidence into the risk analysis, decision, and funding recommendation.",
      activeStep: 3,
      phase: "Recommendation",
    },
    arc_score: {
      title: "Calculating ArcScore",
      detail: "Assessing business health from the completed MCA report and all available internal and external evidence.",
      activeStep: 4,
      phase: "Business health",
    },
    complete: {
      title: "Finalizing the underwriting report",
      detail: "Saving the completed evidence review and recommendation to the company report.",
      activeStep: 4,
      phase: "Finalizing",
    },
  };
  return {
    ...(presentations[stage] || {
      title: "Preparing the underwriting report",
      detail: "Processing the company evidence for this underwriting run.",
      activeStep: Math.max(0, UNDERWRITING_PROGRESS_STEPS.findIndex((step) => step.key === stage)),
      phase: label(stage),
    }),
    stage,
    progress,
  };
}

function UnderwritingThinking({ fullUnderwriting, underwritingJob, title = "Full underwriting output" }) {
  const stage = underwritingJob?.stage || fullUnderwriting?.stage || "queued";
  const progress = underwritingJob?.progress ?? fullUnderwriting?.progress ?? 0;
  const presentation = buildUnderwritingProgressPresentation(stage, progress);
  return (
    <Section
      title={title}
      subtitle="The report refresh is in progress. The previous report remains hidden until the new evidence and recommendation are ready."
    >
      <Paper
        data-testid="underwriting-thinking"
        variant="outlined"
        sx={{
          p: { xs: 2.5, md: 4 },
          borderRadius: 3,
          borderColor: "#cfe0ff",
          bgcolor: "#f7faff",
          textAlign: "center",
        }}
      >
        <CircularProgress size={34} sx={{ mb: 2 }} />
        <Typography sx={{ color: C.ink, fontSize: 20, fontWeight: 950 }}>
          {presentation.title}
        </Typography>
        <Typography sx={{ color: C.muted, mt: 0.75, maxWidth: 700, mx: "auto", lineHeight: 1.6 }}>
          {presentation.detail}
        </Typography>
        <Box sx={{ maxWidth: 760, mx: "auto", mt: 2.5 }}>
          <Box sx={{ height: 7, borderRadius: 999, bgcolor: "#dbe7f8", overflow: "hidden" }}>
            <Box sx={{ width: `${presentation.progress}%`, height: "100%", borderRadius: 999, bgcolor: C.blue, transition: "width 300ms ease" }} />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mt: 1 }}>
            <Typography sx={{ color: C.blue, fontSize: 12, fontWeight: 900 }}>{presentation.phase}</Typography>
            <Typography sx={{ color: C.muted, fontSize: 12, fontWeight: 800 }}>{presentation.progress}% complete</Typography>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1, mt: 2 }}>
            {UNDERWRITING_PROGRESS_STEPS.map((step, index) => {
              const complete = presentation.activeStep > index;
              const active = presentation.activeStep === index;
              return (
                <Box key={step.key} sx={{ display: "flex", alignItems: "center", gap: 0.75, textAlign: "left", minWidth: 0 }}>
                  <Box sx={{ width: 22, height: 22, borderRadius: "50%", display: "grid", placeItems: "center", flex: "0 0 auto", fontSize: 11, fontWeight: 900, color: complete || active ? "#fff" : "#7890a8", bgcolor: complete ? C.green : active ? C.blue : "#e5edf6" }}>
                    {complete ? "✓" : index + 1}
                  </Box>
                  <Typography sx={{ color: active ? C.ink : C.muted, fontSize: 11.5, fontWeight: active ? 900 : 700, lineHeight: 1.25 }}>
                    {step.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
        <Typography sx={{ color: C.muted, mt: 2.25, fontSize: 12 }}>
          This page updates automatically. You can safely leave and return while processing continues.
        </Typography>
      </Paper>
    </Section>
  );
}

function StructuredRecommendationData({ summary }) {
  if (!summary || typeof summary !== "object" || !Object.keys(summary).length) return null;
  const titles = {
    decision: "Decision",
    recommendedAmount: "Recommended amount",
    recommendedFactorRate: "Recommended factor rate",
    recommendedHoldbackPercent: "Recommended holdback",
    recommendedCreditScore: "Underwriting score",
    chosenTier: "Selected tier",
    shortExplanation: "Recommendation summary",
    decisionRationale: "Decision rationale",
    scoreRationale: "Score rationale",
    limitRationale: "Offer rationale",
    conditions: "Conditions",
    internalReviewItems: "Internal review items",
    keyDrivers: "Key evidence drivers",
    whatWouldChangeRecommendation: "What could change the recommendation",
  };
  const displayValue = (key, value) => {
    if (key === "recommendedAmount") return money(value);
    if (key === "recommendedFactorRate") return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
    if (key === "recommendedHoldbackPercent") return `${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
    if (key === "recommendedCreditScore") return `${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })} / 100`;
    if (key === "decision") return label(value);
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "object") return JSON.stringify(value, null, 2);
    return clientSafeAiText(String(value));
  };
  const primaryKeys = ["decision", "recommendedAmount", "recommendedFactorRate", "recommendedHoldbackPercent", "recommendedCreditScore", "chosenTier"];
  const primary = primaryKeys.filter((key) => summary[key] !== null && summary[key] !== undefined && summary[key] !== "");
  const narrativeKeys = ["shortExplanation", "decisionRationale", "scoreRationale", "limitRationale"];
  const narratives = narrativeKeys.filter((key) => summary[key]);
  const listKeys = Object.keys(summary).filter((key) => Array.isArray(summary[key]) && summary[key].length);
  const handled = new Set([...primaryKeys, ...narrativeKeys, ...listKeys]);
  const additional = Object.entries(summary).filter(([key, value]) => !handled.has(key) && value !== null && value !== undefined && value !== "");
  return (
    <Paper variant="outlined" data-testid="structured-underwriting-recommendation" sx={{ p: { xs: 2, md: 2.5 }, mb: 2, minWidth: 0, borderColor: C.line, borderRadius: 3, bgcolor: "#fff", textAlign: "left" }}>
      <Typography sx={{ color: C.ink, fontSize: 16, fontWeight: 950 }}>Structured recommendation</Typography>
      <Typography sx={{ color: C.muted, mt: 0.25, fontSize: 12.5, lineHeight: 1.55 }}>
        All machine-readable recommendation fields returned with this underwriting run.
      </Typography>
      {!!primary.length && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(5, minmax(0, 1fr))" }, gap: 1, mt: 1.5 }}>
          {primary.map((key) => (
            <Box key={key} sx={{ minWidth: 0, p: 1.25, border: `1px solid ${C.line}`, borderRadius: 2, bgcolor: "#f8fafc", textAlign: "left" }}>
              <Typography sx={{ color: C.muted, fontSize: 10, lineHeight: 1.35, fontWeight: 850, textTransform: "uppercase" }}>{titles[key] || label(key)}</Typography>
              <Typography sx={{ color: C.ink, mt: 0.35, fontSize: 14, lineHeight: 1.4, fontWeight: 900, overflowWrap: "anywhere" }}>{displayValue(key, summary[key])}</Typography>
            </Box>
          ))}
        </Box>
      )}
      {!!narratives.length && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: narratives.length > 1 ? "repeat(2, minmax(0, 1fr))" : "1fr" }, gap: 1.25, mt: 1.5 }}>
          {narratives.map((key) => (
            <Box key={key} sx={{ minWidth: 0, p: 1.5, border: `1px solid ${C.line}`, borderRadius: 2, textAlign: "left" }}>
              <Typography sx={{ color: C.blue, fontSize: 10.5, lineHeight: 1.35, fontWeight: 900, textTransform: "uppercase" }}>{titles[key] || label(key)}</Typography>
              <Typography sx={{ color: "#29445f", mt: 0.6, fontSize: 12.5, lineHeight: 1.7, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{displayValue(key, summary[key])}</Typography>
            </Box>
          ))}
        </Box>
      )}
      {(listKeys.length > 0 || additional.length > 0) && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 1.5, mt: 1.5 }}>
          {listKeys.map((key) => (
            <Box key={key} sx={{ minWidth: 0, textAlign: "left" }}>
              <Typography sx={{ color: C.ink, fontSize: 11.5, fontWeight: 900 }}>{titles[key] || label(key)}</Typography>
              <Box component="ul" sx={{ color: "#41556b", mt: 0.65, mb: 0, pl: 2.4, fontSize: 12.5, lineHeight: 1.65 }}>
                {summary[key].map((item, index) => <li key={`${key}-${index}`}>{displayValue(key, item)}</li>)}
              </Box>
            </Box>
          ))}
          {additional.map(([key, value]) => (
            <Box key={key} sx={{ minWidth: 0, textAlign: "left" }}>
              <Typography sx={{ color: C.ink, fontSize: 11.5, fontWeight: 900 }}>{titles[key] || label(key)}</Typography>
              <Typography component="pre" sx={{ m: 0, mt: 0.65, color: "#41556b", fontFamily: "inherit", whiteSpace: "pre-wrap", overflowWrap: "anywhere", fontSize: 12.5, lineHeight: 1.65 }}>{displayValue(key, value)}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
}

function UnderwritingOutput({ fullUnderwriting, underwritingJob, showEmpty = false, title = "Full underwriting output" }) {
  if (!showEmpty && fullUnderwriting?.status !== "done") return null;
  if (["queued", "running"].includes(fullUnderwriting?.status)) {
    return <UnderwritingThinking title={title} fullUnderwriting={fullUnderwriting} underwritingJob={underwritingJob} />;
  }
  if (fullUnderwriting?.status === "score_ready" && fullUnderwriting?.mode === "arcscore_only") {
    return (
      <Section title={title} subtitle="The full report is generated only when this company is opened.">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <CircularProgress size={22} />
          <Typography sx={{ color: C.ink, fontWeight: 800 }}>
            ArcScore is ready. Starting the full underwriting report…
          </Typography>
        </Box>
      </Section>
    );
  }
  const safeReport = normalizeUnderwritingMarkdown(
    clientSafeAiText(fullUnderwriting?.reportMarkdown || "Report content was not retained."),
  );
  const outline = underwritingSectionOutline(safeReport);
  const reportMeta = underwritingReportMeta(safeReport);
  return (
    <Section
      title={title}
      subtitle="The complete result generated by the configured MCA underwriting prompt."
    >
      {fullUnderwriting?.status === "error" && (
        <Alert severity="error">
          {clientSafeAiText(fullUnderwriting.message || "The underwriting job failed. Re-run it to try again.")}
        </Alert>
      )}
      {!fullUnderwriting && showEmpty && (
        <Alert severity="info">
          Refresh the report to collect external evidence and generate the full configured MCA report.
        </Alert>
      )}
      {fullUnderwriting?.status === "done" && (
        <>
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2, md: 2.5 },
              mb: 2,
              borderRadius: 3,
              borderColor: "#cfe0f5",
              background: "linear-gradient(135deg, #f8fbff 0%, #eef5ff 100%)",
              display: "flex",
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", md: "row" },
              gap: 1.5,
            }}
          >
            <Box>
              <Typography sx={{ color: C.ink, fontSize: { xs: 20, md: 23 }, fontWeight: 950, letterSpacing: "-0.02em" }}>
                Full credit underwriting report
              </Typography>
              <Typography sx={{ color: C.muted, fontSize: 13, mt: 0.3 }}>
                Evidence review, risk analysis and supportable funding recommendation
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
              <Chip variant="outlined" label={fullUnderwriting.promptVersion || "MCA policy"} sx={{ bgcolor: "#fff" }} />
              <Chip
                variant="outlined"
                sx={{ bgcolor: "#fff" }}
                label={fullUnderwriting.generatedAt ? new Date(fullUnderwriting.generatedAt).toLocaleString() : "Generated"}
              />
            </Box>
          </Paper>
          {(reportMeta.mode || reportMeta.decision || reportMeta.confidence) && (
            <Paper
              variant="outlined"
              sx={{
                p: 1.4,
                mb: 2,
                borderColor: C.line,
                borderRadius: 2.5,
                bgcolor: "#f8fafc",
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              {reportMeta.mode && <Chip size="small" variant="outlined" label={`Mode · ${reportMeta.mode}`} />}
              {reportMeta.decision && (
                <Chip
                  size="small"
                  label={`Decision · ${reportMeta.decision}`}
                  sx={{ color: color(reportMeta.decision.toLowerCase()), bgcolor: `${color(reportMeta.decision.toLowerCase())}12`, fontWeight: 900 }}
                />
              )}
              {reportMeta.confidence && <Chip size="small" variant="outlined" label={`Confidence · ${reportMeta.confidence}`} />}
            </Paper>
          )}
          <StructuredRecommendationData summary={fullUnderwriting.recommendationSummary} />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: outline.length ? "220px minmax(0, 1fr)" : "1fr" }, gap: 2, alignItems: "start" }}>
            {!!outline.length && (
              <Paper
                component="nav"
                aria-label="Underwriting report sections"
                variant="outlined"
                sx={{
                  display: { xs: "flex", lg: "block" },
                  gap: 0.5,
                  p: 1,
                  borderColor: C.line,
                  borderRadius: 3,
                  overflowX: { xs: "auto", lg: "visible" },
                  position: { lg: "sticky" },
                  top: { lg: 16 },
                }}
              >
                <Typography sx={{ display: { xs: "none", lg: "block" }, px: 1, py: 0.75, color: C.muted, fontSize: 10.5, fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Report sections
                </Typography>
                {outline.map((item, index) => (
                  <Button
                    key={`${item.id}-${index}`}
                    size="small"
                    onClick={() => document.getElementById(item.id)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                    sx={{
                      width: { xs: "auto", lg: "100%" },
                      minWidth: { xs: "max-content", lg: 0 },
                      justifyContent: "flex-start",
                      textAlign: "left",
                      textTransform: "none",
                      color: C.muted,
                      fontSize: 11.5,
                      lineHeight: 1.35,
                      px: 1,
                    }}
                  >
                    <Box component="span" sx={{ color: C.blue, fontWeight: 900, mr: 0.75 }}>{String(index + 1).padStart(2, "0")}</Box>
                    {item.title}
                  </Button>
                ))}
              </Paper>
            )}
            <Paper
              variant="outlined"
              data-testid="full-underwriting-output"
              sx={{
                minWidth: 0,
                color: C.ink,
                textAlign: "left",
                px: { xs: 2, md: 4.5 },
                py: { xs: 2.5, md: 4 },
                borderRadius: 3,
                borderColor: C.line,
                bgcolor: "#fff",
                boxShadow: "0 18px 50px rgba(18, 48, 78, 0.07)",
                overflowWrap: "anywhere",
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={underwritingMarkdownComponents}>
                {safeReport}
              </ReactMarkdown>
            </Paper>
          </Box>
        </>
      )}
    </Section>
  );
}

function CompanyResearchProfile({ entity }) {
  const profile = extractCompanyResearchProfile(entity?.fullUnderwriting?.reportMarkdown);
  if (entity?.fullUnderwriting?.status !== "done" || !profile?.markdown) return null;
  return (
    <Section
      title="Company research profile"
      subtitle="Evidence-led operating narrative from the completed full underwriting report."
    >
      <Paper
        variant="outlined"
        data-testid="company-research-profile"
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          borderColor: C.line,
          bgcolor: "#fff",
          color: C.ink,
          lineHeight: 1.75,
          "& p": { color: C.ink, fontSize: 14, lineHeight: 1.8, mb: 1.5 },
          "& p:last-child": { mb: 0 },
        }}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={underwritingMarkdownComponents}>
          {profile.markdown}
        </ReactMarkdown>
      </Paper>
    </Section>
  );
}

function OfferLiftPanel({ offerLift, busy, invitationUrl, onRefresh, onInvite }) {
  if (!offerLift) return (
    <Section title="OfferLift" subtitle="Create an evidence-based improvement plan for this borrower.">
      <Alert severity="info">Run OfferLift from the report header to evaluate eligibility and create the requested-evidence checklist.</Alert>
    </Section>
  );
  const offer = offerLift.currentOffer || {};
  return (
    <Box>
      <Alert severity="info" sx={{ mb: 1.75, borderRadius: 2.5 }}>
        <Typography sx={{ fontWeight: 800 }}>{label(offerLift.status)}</Typography>
        {offerLift.eligibility?.explanation}
      </Alert>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(5, 1fr)" }, gap: 1.1, mb: 1.75 }}>
        <Metric title="Decision" value={label(offer.decision)} /><Metric title="Amount" value={money(offer.amount)} /><Metric title="Holdback" value={offer.holdbackPercent == null ? "—" : `${offer.holdbackPercent}%`} /><Metric title="Credit score" value={offer.creditScore ?? "—"} /><Metric title="Progress" value={`${offerLift.progress?.percent || 0}%`} />
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.35fr) minmax(280px, 0.75fr)" }, gap: 1.75, alignItems: "start" }}>
        <Section title="Evidence collection" subtitle={`${offerLift.progress?.completed || 0} of ${(offerLift.requirements || []).length} borrower requests completed.`}>
          {(offerLift.requirements || []).map((item) => <ChecklistRow key={item.id} title={item.title} detail={item.why} status={label(item.status)} ready={item.status === "completed"} />)}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.75 }}><Button variant="contained" disabled={busy || !offerLift.eligibility?.eligible} onClick={onInvite}>Generate secure link</Button><Button variant="outlined" disabled={busy} onClick={onRefresh}>Refresh status</Button>{invitationUrl && <Button startIcon={<ContentCopyOutlinedIcon />} onClick={() => navigator.clipboard.writeText(invitationUrl)}>Copy link</Button>}</Box>
          {invitationUrl && <TextField fullWidth size="small" value={invitationUrl} InputProps={{ readOnly: true }} sx={{ mt: 1.5 }} />}
        </Section>
        <Section title="Activity" subtitle="Auditable borrower and team events.">
          {(offerLift.activity || []).length ? (offerLift.activity || []).map((item) => <Box key={item.id} sx={{ position: "relative", pl: 2, py: 0.8, borderLeft: `1px solid ${C.line}`, "&:before": { content: '""', position: "absolute", left: -4.5, top: 15, width: 8, height: 8, borderRadius: "50%", bgcolor: "#5268ad" } }}><Typography sx={{ color: C.ink, fontSize: 12.5, fontWeight: 750 }}>{label(item.type)}</Typography><Typography sx={{ color: C.muted, fontSize: 10.5 }}>{item.actor || "System"} · {new Date(item.createdAt).toLocaleString()}</Typography></Box>) : <Typography sx={{ color: C.muted, fontSize: 12.5 }}>No activity recorded yet.</Typography>}
        </Section>
      </Box>
      {offerLift.disclaimer && <Alert severity="info" sx={{ borderRadius: 2.5 }}>{offerLift.disclaimer}</Alert>}
    </Box>
  );
}

export default function PaymentPlatformCompanyReport({ adminAction = false }) {
  const { entityId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = useSelector((state) => state.userInfo.isAdmin);
  const currentTenantId = useSelector((state) => state.userInfo.tenantId);
  const tenantId =
    isAdmin || adminAction ? getTenantIdFromURL() : currentTenantId;
  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState(0);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [escalationOpen, setEscalationOpen] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamMembersLoading, setTeamMembersLoading] = useState(false);
  const [teamMembersError, setTeamMembersError] = useState("");
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [financialsBusy, setFinancialsBusy] = useState(false);
  const [underwritingJob, setUnderwritingJob] = useState(null);
  const [offerLift, setOfferLift] = useState(null);
  const [offerLiftBusy, setOfferLiftBusy] = useState(false);
  const [offerLiftInvitationUrl, setOfferLiftInvitationUrl] = useState("");
  const [offerLiftShareOpen, setOfferLiftShareOpen] = useState(false);
  const [actionsAnchor, setActionsAnchor] = useState(null);
  const lazyFullReportRequested = useRef(null);
  const [reviewForm, setReviewForm] = useState({
    decision: "approve", notes: "", amount: "", termMonths: "", factorRate: "",
  });
  const [escalationForm, setEscalationForm] = useState({ assigneeUserId: "", notes: "" });

  useEffect(() => {
    let active = true;
    if (!tenantId || !entityId) return undefined;
    setLoading(true);
    setError("");
    getPaymentPlatformEntity(tenantId, entityId)
      .then((data) => {
        if (active) setEntity(data);
      })
      .catch((requestError) => {
        if (active)
          setError(
            requestError?.response?.data?.message ||
              "Could not load the company report",
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [tenantId, entityId]);

  useEffect(() => {
    const compactResult = entity?.fullUnderwriting;
    if (!tenantId || !entityId
      || compactResult?.status !== "score_ready"
      || compactResult?.mode !== "arcscore_only"
      || lazyFullReportRequested.current === entityId) return undefined;
    let active = true;
    lazyFullReportRequested.current = entityId;
    setTab(1);
    startPaymentPlatformFullUnderwriting(tenantId, entityId, { forceFreshUcc: true })
      .then((job) => {
        if (!active) return;
        setUnderwritingJob(job);
        setEntity((current) => ({ ...current, fullUnderwriting: job }));
        toast.info("CrediArc is generating this company’s full underwriting report");
      })
      .catch((requestError) => {
        if (!active) return;
        lazyFullReportRequested.current = null;
        toast.error(requestError?.response?.data?.message || "Could not start the full underwriting report");
      });
    return () => { active = false; };
  }, [tenantId, entityId, entity?.fullUnderwriting?.status, entity?.fullUnderwriting?.mode]);

  useEffect(() => {
    const jobId = underwritingJob?.jobId || entity?.fullUnderwriting?.jobId;
    const status = underwritingJob?.status || entity?.fullUnderwriting?.status;
    if (!tenantId || !jobId || !["queued", "running"].includes(status)) return undefined;
    let active = true;
    const poll = async () => {
      try {
        const job = await getPaymentPlatformFullUnderwritingJob(tenantId, jobId);
        if (!active) return;
        if (job.status === "done") {
          const fallbackEntity = (current) => ({ ...current, arcScore: job.result?.arcScore || current.arcScore, fullUnderwriting: {
            status: "done",
            jobId,
            reportMarkdown: job.result?.reportMarkdown,
            generatedAt: job.result?.generatedAt,
            model: job.result?.model,
            providerLabel: job.result?.providerLabel,
            promptVersion: job.result?.promptVersion,
            recommendationSummary: job.result?.recommendationSummary || null,
            humanReviewRequired: true,
            evidenceDetails: {
              registrationAndUcc: job.result?.evidence?.registrationAndUcc || null,
              financialReporting: financialReportingEvidence(job.result?.evidence?.dnb) || null,
              digitalAndSocial: job.result?.evidence?.digitalAndSocial || null,
            },
            evidenceCoverage: {
              registrationUcc: Object.values(job.result?.evidence?.registrationAndUcc || {}).some((item) => item?.status === "available"),
              financialReporting: job.result?.evidence?.dnb?.status === "available",
              digitalSocial: job.result?.evidence?.digitalAndSocial?.status === "available",
            },
          } });
          const persistedEntity = await getPaymentPlatformEntity(tenantId, entityId).catch(() => null);
          if (!active) return;
          // Update the entity before publishing the terminal job state. Publishing
          // `done` first changes this effect's dependencies and runs its cleanup,
          // which used to cancel the completed-entity fetch and leave the page at
          // 100% "Finalizing" forever even though the report was already stored.
          if (persistedEntity?.fullUnderwriting?.status === "done") setEntity(persistedEntity);
          else setEntity(fallbackEntity);
          setUnderwritingJob(job);
          toast.success("Full MCA underwriting report is ready");
        } else if (job.status === "error") {
          setUnderwritingJob(job);
          setEntity((current) => ({ ...current, fullUnderwriting: {
            ...(current.fullUnderwriting || {}), status: "error", message: job.message,
          } }));
          toast.error(clientSafeAiText(job.message || "Full underwriting failed"));
        } else {
          setUnderwritingJob(job);
        }
      } catch (requestError) {
        if (active) toast.error(requestError?.response?.data?.message || "Could not refresh underwriting status");
      }
    };
    poll();
    const timer = window.setInterval(poll, 4000);
    return () => { active = false; window.clearInterval(timer); };
  }, [tenantId, entity?.fullUnderwriting?.jobId, entity?.fullUnderwriting?.status, underwritingJob?.jobId, underwritingJob?.status]);

  const assessment = entity?.assessment || {};
  const recommendation = assessment.recommendation || {};
  const headlineRecommendation = buildRecommendationPresentation(entity);
  const underwritingStatus = underwritingJob?.status || entity?.fullUnderwriting?.status;
  const underwritingInProgress = ["queued", "running"].includes(underwritingStatus);
  const hasOffer =
    assessment.decision === "approve" && recommendation.amount != null;
  const hasReportOffer =
    ["approved", "approved_with_conditions"].includes(
      headlineRecommendation.decision,
    ) && headlineRecommendation.amount != null;
  const modelValidated = assessment.validation?.status === "validated";
  const requiresManualReview =
    entity?.manualReview?.status === "completed"
      ? false
      : recommendation.requiresManualReview || !modelValidated;
  const openedFromDashboard = location.state?.reportOrigin === "dashboard";
  const backPath = openedFromDashboard
    ? `/tenant-dashboard/${tenantId}`
    : isAdmin || adminAction
      ? `/tenant-dashboard/${tenantId}/assessments`
      : "/assessments";
  const backLabel = openedFromDashboard ? "Dashboard" : "Companies";

  if (loading)
    return (
      <Box sx={{ p: 8, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  if (error || !entity)
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || "Company report not found"}</Alert>
      </Box>
    );
  const displayName =
    entity.identity?.companyName ||
    entity.identity?.externalId ||
    entity.identity?.website ||
    "Unidentified merchant";
  const approvalTermsInvalid = reviewForm.decision === "approve" && (
    Number(reviewForm.amount) <= 0 ||
    Number(reviewForm.termMonths) <= 0 ||
    Number(reviewForm.factorRate) < 1
  );
  const openReview = (decision = "approve") => {
    setReviewForm({
      decision,
      notes: "",
      amount: entity.manualReview?.amount ?? recommendation.amount ?? entity.signals?.requestedAmount ?? "",
      termMonths: entity.manualReview?.termMonths ?? recommendation.termMonths ?? 12,
      factorRate: entity.commercialTerms?.locked && entity.commercialTerms.factorRate != null
        ? entity.commercialTerms.factorRate
        : entity.manualReview?.factorRate ?? recommendation.factorRate ?? 1.18,
    });
    setReviewOpen(true);
  };
  const openEscalation = async () => {
    setEscalationForm({ assigneeUserId: "", notes: "" });
    setTeamMembersError("");
    setEscalationOpen(true);
    setTeamMembersLoading(true);
    try {
      const result = await listQuickTaskAssignees(tenantId);
      const currentUserId = getCurrentUserId();
      setTeamMembers((result?.data || result || []).filter((member) => member.id !== currentUserId));
    } catch (requestError) {
      setTeamMembersError(requestError?.response?.data?.error || "Could not load team members");
    } finally {
      setTeamMembersLoading(false);
    }
  };
  const saveEscalation = async () => {
    const assignee = teamMembers.find((member) => member.id === escalationForm.assigneeUserId);
    if (!assignee || !escalationForm.notes.trim()) return;
    setActionBusy(true);
    try {
      await createQuickTask(tenantId, {
        companyId: entity.id || entityId,
        companyName: displayName,
        assigneeUserId: assignee.id,
        assigneeName: assignee.name,
        taskType: "approval",
        action: "Review company report",
        reason: escalationForm.notes.trim(),
      });
      setEscalationOpen(false);
      toast.success(`Report escalated to ${assignee.name}`);
    } catch (requestError) {
      toast.error(requestError?.response?.data?.error || "Could not escalate the report");
    } finally {
      setActionBusy(false);
    }
  };
  const saveReview = async () => {
    if (!reviewForm.notes.trim()) {
      toast.error("Review notes are required");
      return;
    }
    if (approvalTermsInvalid) {
      toast.error("Approved amount, term, and factor rate are required for a manual approval");
      return;
    }
    setActionBusy(true);
    try {
      const updated = await reviewPaymentPlatformEntity(tenantId, entityId, reviewForm);
      setEntity(updated);
      setReviewOpen(false);
      toast.success("Manual review recorded");
    } catch (requestError) {
      toast.error(requestError?.response?.data?.message || "Could not record the manual review");
    } finally {
      setActionBusy(false);
    }
  };
  const updateArchive = async () => {
    setActionBusy(true);
    try {
      const updated = await setPaymentPlatformEntityArchived(tenantId, entityId, !entity.archived);
      setEntity(updated);
      setArchiveOpen(false);
      toast.success(updated.archived ? "Company archived" : "Company restored");
    } catch (requestError) {
      toast.error(requestError?.response?.data?.message || "Could not update the archive status");
    } finally {
      setActionBusy(false);
    }
  };
  const refreshReport = async () => {
    const previousFullUnderwriting = entity.fullUnderwriting;
    setRefreshing(true);
    setUnderwritingJob({ status: "queued", stage: "queued", progress: 0 });
    setEntity((current) => ({
      ...current,
      fullUnderwriting: { status: "queued", stage: "queued", progress: 0 },
    }));
    setTab(1);
    try {
      const job = await startPaymentPlatformFullUnderwriting(tenantId, entityId, { forceFreshUcc: true });
      const latest = await getPaymentPlatformEntity(tenantId, entityId);
      setUnderwritingJob(job);
      setEntity({ ...latest, fullUnderwriting: job });
      setError("");
      toast.info("CrediArc is refreshing this company’s underwriting report");
    } catch (requestError) {
      setUnderwritingJob(null);
      setEntity((current) => ({ ...current, fullUnderwriting: previousFullUnderwriting }));
      toast.error(
        requestError?.response?.data?.message ||
          "Could not start the company refresh",
      );
    } finally {
      setRefreshing(false);
    }
  };

  const getFinancials = async () => {
    setFinancialsBusy(true);
    try {
      const result = await getPaymentPlatformEntityFinancials(tenantId, entityId);
      if (result?.entity) setEntity(result.entity);
      else setEntity((current) => ({ ...current, financialReporting: result?.financialReporting }));
      setTab(4);
      toast.success("Financials retrieved by CrediArc");
    } catch (requestError) {
      toast.error(requestError?.response?.data?.message || "CrediArc could not retrieve financials for this entity");
    } finally {
      setFinancialsBusy(false);
    }
  };

  const loadOfferLift = async () => {
    setOfferLiftBusy(true);
    try { setOfferLift(await getPaymentPlatformOfferLift(tenantId, entityId)); }
    catch (requestError) { toast.error(requestError?.response?.data?.message || "Could not load OfferLift"); }
    finally { setOfferLiftBusy(false); }
  };
  const runOfferLift = async () => {
    setOfferLiftBusy(true);
    try {
      const offerLiftCase = await startPaymentPlatformOfferLift(tenantId, entityId);
      setOfferLift(offerLiftCase);
      if (!offerLiftCase?.eligibility?.eligible) {
        setTab(5);
        toast.info("OfferLift eligibility review completed");
        return;
      }
      const result = await createPaymentPlatformOfferLiftInvitation(tenantId, entityId, { expiresInDays: 7, channel: "copy_link", baseUrl: window.location.origin });
      setOfferLiftInvitationUrl(result.url);
      setOfferLift((current) => ({ ...current, status: "invited", invitation: result.invitation }));
      setOfferLiftShareOpen(true);
      toast.success("Secure OfferLift link created");
    } catch (requestError) { toast.error(requestError?.response?.data?.message || "Could not start OfferLift"); }
    finally { setOfferLiftBusy(false); }
  };
  const createOfferLiftInvite = async () => {
    setOfferLiftBusy(true);
    try {
      const result = await createPaymentPlatformOfferLiftInvitation(tenantId, entityId, { expiresInDays: 7, channel: "copy_link", baseUrl: window.location.origin });
      setOfferLiftInvitationUrl(result.url);
      setOfferLift((current) => ({ ...current, status: "invited", invitation: result.invitation }));
      setOfferLiftShareOpen(true);
      toast.success("Secure OfferLift link created");
    } catch (requestError) { toast.error(requestError?.response?.data?.message || "Could not create OfferLift link"); }
    finally { setOfferLiftBusy(false); }
  };

  return (
    <Box sx={{ bgcolor: "#f4f6fa", minHeight: "100%", pb: 4 }}>
      <Paper square elevation={0} sx={{ bgcolor: "#fff", borderBottom: `1px solid ${C.line}` }}>
        <Box sx={{ px: { xs: 2, md: 3 }, pt: 1.5, maxWidth: 1500, mx: "auto" }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(backPath)} sx={{ mb: 1, px: 0, color: C.muted, fontSize: 12, textTransform: "none", "&:hover": { bgcolor: "transparent", color: C.blue } }}>
            {backLabel}&nbsp; / &nbsp;Company report
          </Button>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography component="h1" sx={{ color: C.ink, fontSize: { xs: 23, md: 26 }, lineHeight: 1.2, fontWeight: 850, letterSpacing: "-0.025em" }}>{displayName}</Typography>
              <Typography sx={{ color: C.muted, fontSize: 12.5, mt: 0.5 }}>
                Wix merchant · {entity.identity?.externalId || entity.identity?.registrationNumber || entity.id}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: { xs: "row", sm: "column" }, alignItems: { xs: "center", sm: "flex-end" }, gap: 0.6, flexWrap: "wrap" }}>
              <Chip
                size="small"
                label={underwritingInProgress ? "Refreshing" : entity.archived ? "Archived" : label(entity.manualReview?.status === "completed" ? entity.manualReview.decision : assessment.decision || "Unscored")}
                icon={underwritingInProgress ? <CircularProgress size={12} /> : undefined}
                sx={{ color: underwritingInProgress ? C.blue : color(entity.manualReview?.decision || assessment.decision), bgcolor: underwritingInProgress ? `${C.blue}10` : `${color(entity.manualReview?.decision || assessment.decision)}12`, fontWeight: 850 }}
              />
              {entity.fullUnderwriting?.generatedAt && !underwritingInProgress && (
                <Typography sx={{ color: C.muted, fontSize: 11.5 }}>Last refreshed {new Date(entity.fullUnderwriting.generatedAt).toLocaleDateString()}</Typography>
              )}
            </Box>
          </Box>
          {!underwritingInProgress && (
            <>
              <Box
                aria-label="Company decision summary"
                sx={{
                  mt: 2,
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "minmax(280px, 1.25fr) minmax(170px, 0.65fr) auto" },
                  alignItems: "stretch",
                  border: `1px solid ${headlineRecommendation.color}35`,
                  borderLeft: `5px solid ${headlineRecommendation.color}`,
                  borderRadius: 2.5,
                  overflow: "hidden",
                  bgcolor: `${headlineRecommendation.color}06`,
                }}
              >
                <Box sx={{ px: { xs: 2, md: 2.5 }, py: 2 }}>
                  <Typography sx={{ color: headlineRecommendation.color, fontSize: 10.5, fontWeight: 900, letterSpacing: "0.045em" }}>UNDERWRITING RECOMMENDATION</Typography>
                  <Typography sx={{ mt: 0.65, color: headlineRecommendation.color, fontSize: { xs: 25, md: 30 }, lineHeight: 1.1, fontWeight: 950, letterSpacing: "-0.02em" }}>{headlineRecommendation.label}</Typography>
                  <Typography sx={{ mt: 0.6, color: C.muted, fontSize: 12.5 }}>{requiresManualReview ? "Human action needed" : "Review complete"}</Typography>
                </Box>
                <Box sx={{ px: { xs: 2, md: 2.5 }, py: 2, borderLeft: { xs: 0, md: `1px solid ${headlineRecommendation.color}20` }, borderTop: { xs: `1px solid ${headlineRecommendation.color}18`, md: 0 }, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <Typography sx={{ color: C.muted, fontSize: 11.5, fontWeight: 700 }}>Recommended offer</Typography>
                  <Typography sx={{ mt: 0.4, color: C.ink, fontSize: { xs: 25, md: 30 }, lineHeight: 1.15, fontWeight: 950 }}>{headlineRecommendation.amount == null ? "—" : money(headlineRecommendation.amount)}</Typography>
                </Box>
                {!entity.archived && (
                  <Box sx={{ px: { xs: 2, md: 2.5 }, py: 2, display: "flex", alignItems: "center", justifyContent: { xs: "flex-start", md: "flex-end" }, gap: 1, flexWrap: "wrap" }}>
                    <Button variant="contained" startIcon={<RateReviewOutlinedIcon />} onClick={() => openReview("approve")} sx={{ textTransform: "none", borderRadius: 2, whiteSpace: "nowrap" }}>Approve manually</Button>
                    <Button variant="outlined" startIcon={<ForwardToInboxOutlinedIcon />} onClick={openEscalation} sx={{ textTransform: "none", borderRadius: 2, whiteSpace: "nowrap", bgcolor: "#fff" }}>Escalate to teammate</Button>
                  </Box>
                )}
              </Box>
              <Box sx={{ mt: 1.25, p: { xs: 1.5, md: 1.75 }, display: "flex", alignItems: { xs: "stretch", md: "center" }, gap: { xs: 1, md: 0 }, flexDirection: { xs: "column", md: "row" }, border: `1px solid ${C.line}`, borderRadius: 2.5, bgcolor: "#fff" }}>
                <Box sx={{ display: "flex", flex: "1 1 auto", flexWrap: { xs: "wrap", md: "nowrap" }, minWidth: 0 }}>
                  <HeaderMetric title="Credit score" value={`${headlineRecommendation.creditScore ?? "—"} / 100`} progress={headlineRecommendation.creditScore} />
                  <HeaderMetric title="Evidence coverage" value={`${assessment.coverage || 0}%`} progress={assessment.coverage || 0} />
                  <HeaderMetric title="Identity match" value={`${Math.round((entity.match?.confidence || 0) * 100)}%`} progress={Math.round((entity.match?.confidence || 0) * 100)} />
                </Box>
                <Box sx={{ pl: { md: 2 }, display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
                  <Button size="small" startIcon={<RefreshOutlinedIcon />} disabled={refreshing} onClick={refreshReport} sx={{ textTransform: "none", borderRadius: 2, whiteSpace: "nowrap" }}>{refreshing ? "Refreshing…" : "Refresh report"}</Button>
                  <Button size="small" startIcon={financialsBusy ? <CircularProgress size={14} /> : <AccountBalanceOutlinedIcon />} disabled={financialsBusy || underwritingInProgress} onClick={getFinancials} sx={{ textTransform: "none", borderRadius: 2, whiteSpace: "nowrap" }}>{financialsBusy ? "Getting financials…" : "Get financials"}</Button>
                  {!entity.archived && <Button size="small" startIcon={<TrendingUpOutlinedIcon />} disabled={offerLiftBusy} onClick={runOfferLift} sx={{ textTransform: "none", borderRadius: 2, whiteSpace: "nowrap" }}>{offerLiftBusy ? "Running OfferLift…" : "OfferLift"}</Button>}
                  <Button aria-label="More company actions" size="small" endIcon={<MoreHorizIcon />} onClick={(event) => setActionsAnchor(event.currentTarget)} sx={{ textTransform: "none", borderRadius: 2, whiteSpace: "nowrap" }}>Report actions</Button>
                  <Menu anchorEl={actionsAnchor} open={Boolean(actionsAnchor)} onClose={() => setActionsAnchor(null)}>
                    <MenuItem onClick={() => { setActionsAnchor(null); setArchiveOpen(true); }}><Box sx={{ color: entity.archived ? C.blue : C.amber, display: "flex", alignItems: "center" }}>{entity.archived ? <RestoreOutlinedIcon fontSize="small" /> : <ArchiveOutlinedIcon fontSize="small" />}</Box><Box component="span" sx={{ ml: 1 }}>{entity.archived ? "Restore company" : "Archive company"}</Box></MenuItem>
                  </Menu>
                </Box>
              </Box>
            </>
          )}
          <Tabs value={tab} onChange={(event, value) => setTab(value)} variant="scrollable" scrollButtons="auto" sx={{ mt: 1.25, minHeight: 44, "& .MuiTab-root": { minHeight: 44, px: 1.5, textTransform: "none", fontSize: 12.5, fontWeight: 650 }, "& .Mui-selected": { color: C.ink } }}>
            <Tab label="Decision & offer" />
            <Tab label="Full underwriting" />
            <Tab label="Identity & verification" />
            <Tab label="Applications & outcomes" />
            <Tab label="Platform & financial data" />
            <Tab label="OfferLift" onClick={() => { if (!offerLift) loadOfferLift(); }} />
          </Tabs>
        </Box>
      </Paper>
      <Box sx={{ px: { xs: 2, md: 3 }, pt: 2.25, maxWidth: 1500, mx: "auto" }}>
      {entity.manualReview?.status === "completed" && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <strong>Manual review completed:</strong>{" "}
          {label(entity.manualReview.decision)} by {
            (typeof entity.manualReview.reviewedBy === "object"
              ? entity.manualReview.reviewedBy?.name || entity.manualReview.reviewedBy?.email
              : entity.manualReview.reviewedBy) || "an authorized reviewer"
          }
          {entity.manualReview.reviewedAt ? ` on ${new Date(entity.manualReview.reviewedAt).toLocaleString()}` : ""}. {entity.manualReview.notes}
        </Alert>
      )}
      {entity.archived && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This company is archived and excluded from assessment lists and portfolio totals. Restore it to record another review.
        </Alert>
      )}
        <Box>
          {tab === 0 && (
            underwritingInProgress ? (
              <UnderwritingThinking
                title="Refreshing full underwriting"
                fullUnderwriting={entity.fullUnderwriting}
                underwritingJob={underwritingJob}
              />
            ) : <>
              <TabIntro title="Decision & offer" subtitle="What the underwriter needs to decide, in one focused view." />
              {!modelValidated && (
                <Alert severity="info" sx={{ mb: 1.5, borderRadius: 2.5 }}>
                  <strong>{entity.manualReview?.status === "completed" ? "Draft model result — manual decision recorded:" : "Draft policy simulation — human review required:"}</strong>{" "}
                  this model result is not a validated prediction and must not trigger automated funding. {assessment.validation?.reason}
                </Alert>
              )}
              {!hasOffer && !hasReportOffer && (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: 2.5 }}>
                  <strong>No commercial offer generated.</strong>{" "}
                  {assessment.decision === "insufficient_data" ? "Collect the missing evidence and re-run the assessment." : "Complete the required decision review before taking a funding action."}
                </Alert>
              )}
              <ArcScoreCard arcScore={entity.arcScore} underwritingStatus={underwritingStatus} />
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.45fr) minmax(280px, 0.8fr)" }, gap: 1.75, alignItems: "start" }}>
                <RecommendationSummary entity={entity} />
                <DecisionChecklist entity={entity} />
              </Box>
              <RecommendationExplainability entity={entity} />
              <CompanyResearchProfile entity={entity} />
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" }, gap: 1.75, alignItems: "start" }}>
                <ScoreExplainability entity={entity} />
                <LimitExplainability entity={entity} />
              </Box>
              <Section
                title="Score drivers"
                subtitle="Every feature used in the score, including its normalized value and configured weight."
              >
                {(assessment.evidence || []).length ? (
                  <Box
                    component="table"
                    sx={{
                      width: "100%",
                      borderCollapse: "collapse",
                      "th,td": {
                        p: 1.2,
                        borderBottom: `1px solid ${C.line}`,
                        textAlign: "left",
                        fontSize: 13,
                      },
                      th: {
                        color: C.muted,
                        fontSize: 11,
                        textTransform: "uppercase",
                      },
                    }}
                  >
                    <thead>
                      <tr>
                        <th>Evidence</th>
                        <th>Normalized value</th>
                        <th>Weight</th>
                        <th>Weighted contribution</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assessment.evidence.map((item) => (
                        <tr key={item.feature}>
                          <td>
                            <strong>{label(item.feature)}</strong>
                          </td>
                          <td>{valueText(item.value)}</td>
                          <td>{item.weight}</td>
                          <td>{Number(item.value * item.weight).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Box>
                ) : (
                  <Typography sx={{ color: C.muted }}>
                    No score evidence was available.
                  </Typography>
                )}
              </Section>
              <Section title="Missing evidence">
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {(assessment.missingFeatures || []).map((feature) => (
                    <Chip
                      key={feature}
                      size="small"
                      label={label(feature)}
                      variant="outlined"
                    />
                  ))}
                  {!assessment.missingFeatures?.length && (
                    <Typography sx={{ color: C.muted }}>
                      No configured evidence is missing.
                    </Typography>
                  )}
                </Box>
              </Section>
            </>
          )}
          {tab === 1 && (
            <>
              <TabIntro title="Full underwriting" subtitle="Complete evidence review, risk analysis, and funding recommendation. Use Refresh report to generate a new result." />
              <UnderwritingOutput
                fullUnderwriting={entity.fullUnderwriting}
                underwritingJob={underwritingJob}
                showEmpty
              />
              {!underwritingInProgress && (
                <>
                  {entity.fullUnderwriting?.evidenceCoverage && <Section title="Evidence source coverage"><FieldGrid data={evidenceCoverageLabels(entity.fullUnderwriting.evidenceCoverage)} /></Section>}
                  <UccFreshnessSummary fullUnderwriting={entity.fullUnderwriting} />
                  <EvidenceDetails
                    title="Registration & UCC evidence"
                    subtitle="Official registry results, filing rows, search status and source limitations used by the report."
                    data={entity.fullUnderwriting?.evidenceDetails?.registrationAndUcc}
                  />
                  {(entity.fullUnderwriting?.evidenceDetails?.financialReporting || entity.fullUnderwriting?.evidenceDetails?.dnb)?.status === "available" ? (
                    <EvidenceDetails
                      title="Financial reporting"
                      subtitle="Company profile, financial statements, strength indicators and matching data provided by CrediArc."
                      data={financialReportingEvidence(entity.fullUnderwriting.evidenceDetails.financialReporting || entity.fullUnderwriting.evidenceDetails.dnb)}
                    />
                  ) : (
                    <Section
                      title="Financial reporting"
                      subtitle="Company profile, financial statements, strength indicators and matching data returned for this report."
                    >
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        No financial reporting data was returned for the completed underwriting run. Refresh the report to retry the lookup with the latest company information.
                      </Alert>
                    </Section>
                  )}
                  <DigitalSocialEvidenceSummary
                    data={entity.fullUnderwriting?.evidenceDetails?.digitalAndSocial}
                    reportText={entity.fullUnderwriting?.reportMarkdown}
                  />
                </>
              )}
            </>
          )}
          {tab === 2 && (
            <><TabIntro title="Identity & verification" subtitle="Resolve identity gaps before using this entity in a funding decision." /><IdentityVerificationPanel entity={entity} assessment={assessment} /></>
          )}
          {tab === 3 && (
            <><TabIntro title="Applications & outcomes" subtitle="Every request, decision, funded term, and observed outcome in one auditable history." /><ApplicationsOutcomesPanel entity={entity} /></>
          )}
          {tab === 4 && (
            <>
              <TabIntro title="Platform & financial data" subtitle="Normalized Wix signals shown in decision-ready units." />
              {(entity.financialReporting || entity.fullUnderwriting?.evidenceDetails?.financialReporting || entity.fullUnderwriting?.evidenceDetails?.dnb)?.status === "available" ? (
                <EvidenceDetails
                  title="Financials by CrediArc"
                  subtitle="On-demand company financial statements, profile metrics and financial-strength indicators."
                  data={financialReportingEvidence(entity.financialReporting || entity.fullUnderwriting?.evidenceDetails?.financialReporting || entity.fullUnderwriting?.evidenceDetails?.dnb)}
                />
              ) : (
                <Section title="Financials by CrediArc" subtitle="On-demand company financial statements and financial-strength indicators.">
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                    <Typography sx={{ color: C.muted, fontSize: 13 }}>No on-demand financials have been retrieved for this entity.</Typography>
                    <Button variant="outlined" startIcon={financialsBusy ? <CircularProgress size={14} /> : <AccountBalanceOutlinedIcon />} disabled={financialsBusy} onClick={getFinancials}>{financialsBusy ? "Getting financials…" : "Get financials"}</Button>
                  </Box>
                </Section>
              )}
              <LockedCommercialTerms terms={entity.commercialTerms} />
              <BankStatementEvidence statements={entity.bankStatements} summary={entity.bankStatementAnalysis} />
              <PlatformKpis entity={entity} />
              <Section title="Normalized signal ranges" subtitle="Human-readable view of the aggregates used by the model.">
                <SignalStatsTable stats={entity.signalStats} />
              </Section>
              <Section title="Normalized platform and financial signals"><DataRows data={entity.signals} /></Section>
              <Section title="Month-by-month performance" subtitle="Manual monthly evidence is preserved here; normalized aggregates above are used by the current model."><MonthlyPerformanceTable rows={entity.performanceHistory} /></Section>
            </>
          )}
          {tab === 5 && (
            <><TabIntro title="OfferLift" subtitle="Collect the smallest set of evidence that could support reconsideration." /><OfferLiftPanel offerLift={offerLift} busy={offerLiftBusy} invitationUrl={offerLiftInvitationUrl} onRefresh={loadOfferLift} onInvite={createOfferLiftInvite} /></>
          )}
        </Box>
      </Box>
      <Dialog open={offerLiftShareOpen} onClose={() => setOfferLiftShareOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Send OfferLift to the client</DialogTitle>
        <DialogContent dividers>
          <Alert severity="success" sx={{ mb: 2 }}>
            The secure borrower link is ready. It expires automatically and can be shared directly with the client.
          </Alert>
          <TextField
            autoFocus
            fullWidth
            label="Client link"
            value={offerLiftInvitationUrl}
            InputProps={{ readOnly: true }}
            onFocus={(event) => event.target.select()}
          />
          {offerLift?.invitation?.expiresAt && <Typography sx={{ color: C.muted, fontSize: 13, mt: 1.25 }}>
            Expires {new Date(offerLift.invitation.expiresAt).toLocaleString()}
          </Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOfferLiftShareOpen(false)}>Close</Button>
          <Button variant="outlined" disabled={!offerLiftInvitationUrl} onClick={() => window.open(offerLiftInvitationUrl, "_blank", "noopener,noreferrer")}>Open client page</Button>
          <Button variant="contained" startIcon={<ContentCopyOutlinedIcon />} disabled={!offerLiftInvitationUrl} onClick={async () => {
            await navigator.clipboard.writeText(offerLiftInvitationUrl);
            toast.success("OfferLift link copied");
          }}>Copy link</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={reviewOpen} onClose={() => !actionBusy && setReviewOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Record manual review</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This decision overrides the displayed model recommendation and is retained in the review history with the reviewer and timestamp.
          </Alert>
          <Box sx={{ display: "grid", gap: 2, pt: .5 }}>
            <TextField select label="Manual decision" value={reviewForm.decision} onChange={(event) => setReviewForm((current) => ({ ...current, decision: event.target.value }))}>
              <MenuItem value="approve">Approve</MenuItem>
              <MenuItem value="review">Keep in review</MenuItem>
              <MenuItem value="decline">Decline</MenuItem>
            </TextField>
            {reviewForm.decision === "approve" && (
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 1.25 }}>
                <TextField required type="number" label="Approved amount" value={reviewForm.amount} onChange={(event) => setReviewForm((current) => ({ ...current, amount: event.target.value }))} />
                <TextField required type="number" label="Term in months" value={reviewForm.termMonths} onChange={(event) => setReviewForm((current) => ({ ...current, termMonths: event.target.value }))} />
                <TextField required disabled={entity.commercialTerms?.locked && entity.commercialTerms.factorRate != null} type="number" label="Factor rate" value={reviewForm.factorRate} inputProps={{ min: 1, step: .01 }} helperText={entity.commercialTerms?.locked && entity.commercialTerms.factorRate != null ? "Locked client-supplied term" : ""} onChange={(event) => setReviewForm((current) => ({ ...current, factorRate: event.target.value }))} />
              </Box>
            )}
            <TextField required multiline minRows={4} label="Review notes" placeholder="Document the evidence checked and the reason for this decision." value={reviewForm.notes} onChange={(event) => setReviewForm((current) => ({ ...current, notes: event.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button disabled={actionBusy} onClick={() => setReviewOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={actionBusy || !reviewForm.notes.trim() || approvalTermsInvalid} onClick={saveReview}>
            {actionBusy ? "Saving…" : "Record decision"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={escalationOpen} onClose={() => !actionBusy && setEscalationOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Escalate report to a teammate</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            The selected teammate will receive this company report in their task queue with your note and a link back to the review.
          </Alert>
          <Box sx={{ display: "grid", gap: 2, pt: .5 }}>
            {teamMembersError && <Alert severity="error">{teamMembersError}</Alert>}
            <TextField
              select
              required
              disabled={teamMembersLoading || Boolean(teamMembersError)}
              label="Assign to"
              value={escalationForm.assigneeUserId}
              onChange={(event) => setEscalationForm((current) => ({ ...current, assigneeUserId: event.target.value }))}
              helperText={teamMembersLoading ? "Loading team members…" : !teamMembers.length && !teamMembersError ? "No other active team members are available." : "Choose the teammate who should make the next decision."}
            >
              {teamMembers.map((member) => (
                <MenuItem key={member.id} value={member.id}>
                  {member.name}{member.email && member.email !== member.name ? ` · ${member.email}` : ""}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              required
              multiline
              minRows={4}
              label="Escalation note"
              placeholder="Explain what needs review and why you are escalating this company."
              value={escalationForm.notes}
              onChange={(event) => setEscalationForm((current) => ({ ...current, notes: event.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button disabled={actionBusy} onClick={() => setEscalationOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={actionBusy || !escalationForm.assigneeUserId || !escalationForm.notes.trim()} onClick={saveEscalation}>
            {actionBusy ? "Escalating…" : "Escalate report"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={archiveOpen} onClose={() => !actionBusy && setArchiveOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{entity.archived ? "Restore company?" : "Archive company?"}</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ color: C.muted }}>
            {entity.archived
              ? "The company will return to assessment lists and portfolio totals."
              : "The company will be removed from normal assessment lists and portfolio totals. Its report and audit history will be retained and it can be restored later."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={actionBusy} onClick={() => setArchiveOpen(false)}>Cancel</Button>
          <Button variant="contained" color={entity.archived ? "primary" : "warning"} disabled={actionBusy} onClick={updateArchive}>
            {actionBusy ? "Saving…" : entity.archived ? "Restore company" : "Archive company"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
